<template>
  <v-main class="bg-content">
    <!-- 🔹 2-screen tab overlay ABOVE everything -->
      <div class="screens-layout">
        <!-- Screen 1 -->
        <div class="screen screen-1">
          <div class="rc-tabs">
            <div
              v-for="t in tabs.filter(tab => (tab.screenId || 1) === 1)"
              :key="t.id"
              class="rc-tab"
              :class="{ active: t.id === activeTabId }"
              @click="activeTabId = t.id"
            >
              <span class="dot" :class="t.status"></span>
              <span class="label" :title="t.title || t.url">
                {{ t.title || prettyUrl(t.url) }}
              </span>
              <v-btn x-small icon @click.stop="closeTab(t.id)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>

            <div
              v-if="!tabs.some(tab => (tab.screenId || 1) === 1)"
              class="rc-empty"
            >
              No tabs on Screen 1 yet.
            </div>

            <div class="spacer"></div>
          </div>
        </div>

        <!-- Screen 2 -->
        <div class="screen screen-2">
          <div class="rc-tabs">
            <div
              v-for="t in tabs.filter(tab => tab.screenId === 2)"
              :key="t.id"
              class="rc-tab"
              :class="{ active: t.id === activeTabId }"
              @click="activeTabId = t.id"
            >
              <span class="dot" :class="t.status"></span>
              <span class="label" :title="t.title || t.url">
                {{ t.title || prettyUrl(t.url) }}
              </span>
              <v-btn x-small icon @click.stop="closeTab(t.id)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>

            <div
              v-if="!tabs.some(tab => tab.screenId === 2)"
              class="rc-empty"
            >
              No tabs on Screen 2 yet.
            </div>

            <div class="spacer"></div>
          </div>
        </div>
      </div>
      <!-- 🔹 END screens overlay -->
      <br />
    <div
      class="chat-container"
      :class="[
        loading ? 'is-thinking' : '',
        stage === 'search' ? 'stage-search' : '',
        stage === 'extract' ? 'stage-extract' : '',
        stage === 'compose' ? 'stage-compose' : '',
        showAnswering ? 'is-answering' : '',
        (recognizing && !loading) ? 'is-speaking' : ''
      ]"
    >
    

      <div class="voice-cta-wrap">
        <v-btn
          class="voice-cta"
          :color="recognizing ? 'red darken-1' : 'teal lighten-1'"
          :disabled="!voiceSupported"
          @click="toggleVoice"
        >
          <v-icon left>{{ recognizing ? 'mdi-stop-circle' : 'mdi-microphone' }}</v-icon>
          {{ recognizing ? 'Stop Listening' : 'Start Speaking' }}
        </v-btn>
      </div>
      <div class="voice-cta-wrap">
        <small v-if="voiceSupported" class="voice-hint">
          Tip: Press <kbd>Space</kbd> anywhere to {{ recognizing ? 'stop' : 'start' }}.
        </small>
        <small v-else class="voice-hint warn">
          Your browser doesn't support voice input. Try Chrome.
        </small>
      </div>
      <v-divider />
      
      <div class="stage-chips">
        <v-chip :class="{on: stage==='search'}" small>🔎 Search</v-chip>
        <v-chip :class="{on: stage==='extract'}" small>🧩 Extract</v-chip>
        <v-chip :class="{on: stage==='compose'}" small>✍️ Compose</v-chip>
      </div>

      <v-card v-if="mode==='general'" class="section-card neon" outlined>
        <div class="input-row">
          <v-textarea
            v-model="question"
            ref="questionBox"
            placeholder='e.g., “Browse macheenist.com and research what they offer for services.”'
            class="question-box"
            auto-grow
            outlined
            rows="3"
            @keydown.ctrl.k.prevent="focusInput"
            @keydown.meta.k.prevent="focusInput"
          />
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                class="ask-btn"
                color="primary"
                :loading="loading"
                :disabled="loading"
                @click="runGeneral"
              >
                Ask AI
              </v-btn>
            </template>
            <span>Submit (Enter) • Toggle mic (Space)</span>
          </v-tooltip>
          
        </div>

        <div class="stage-chips compact">
          <v-chip small :class="{on: voiceStage==='listening'}">🎤 Listening</v-chip>
          <v-chip small :class="{on: voiceStage==='processing'}">⚙️ Processing</v-chip>
          <v-chip small :class="{on: voiceStage==='reporting'}">📰 Reporting</v-chip>
        </div>

        <div class="voice-caption" v-if="recognizing || interimTranscript || finalTranscript" aria-live="polite">
          <div class="cap-title">
            <v-icon x-small left>mdi-waveform</v-icon>
            {{ recognizing ? 'Listening…' : (finalTranscript ? 'Final' : 'Idle') }}
            <span v-if="!voiceSupported" class="warn">Voice not supported</span>
          </div>
          <div class="cap-line interim" v-if="interimTranscript && recognizing">
            <span>{{ interimTranscript }}</span>
          </div>
          <div class="cap-line">
            <span class="final" v-if="finalTranscript">{{ finalTranscript }}</span>
          </div>
        </div>

        <v-switch v-model="autoResearch" inset label="Auto research the web if needed" class="mt-2" />
        <v-switch v-model="useBrowserAgent" inset label="Use Browser Agent (Playwright via server)" class="mt-2" />

        <div class="agent-controls" v-if="useBrowserAgent">
          <v-btn
            :loading="loadingPlan"
            :disabled="loading || loadingPlan || !question.trim()"
            color="deep-purple darken-1"
            class="mr-2"
            @click="(async() => { loadingPlan = true; try { const d = await callBrowserPlanner(false); browserPlan = d?.plan || {steps:[]}; showPlanDialog = true; await mirrorBrowserSearchToConsole(browserPlan); } catch(e){ const msg=e?.message||'Unknown error'; error.message = msg; log && log('Plan failed: '+msg);} finally { loadingPlan=false; } })()"
          >
            🧭 Plan Browser Steps
          </v-btn>

          <v-btn
            :loading="loadingRun"
            :disabled="loading || loadingRun || !question.trim()"
            color="deep-purple"
            outlined
            @click="approveAndRunPlan"
          >
            ▶️ Plan & Run
          </v-btn>

          <div class="agent-status" v-if="agentView">
            <v-chip :color="agentView.statusColor" dark>{{ agentView.statusLabel }}</v-chip>
            <span v-if="agentView.steps?.length" class="muted small ml-2">
              Step {{ agentView.steps.findIndex(s=>s.status==='running') + 1 || 1 }} / {{ agentView.steps.length }}
            </span>
          </div>
        </div>

        <v-dialog v-model="showPlanDialog" max-width="880">
          <v-card>
            <v-card-title class="headline">Browser Plan (Preview)</v-card-title>
            <v-card-text>
              <div v-if="agentView.steps?.length">
                <ol class="mb-3 agent-steps">
                  <li
                    v-for="(s,i) in agentView.steps"
                    :key="'step-'+i"
                    :class="['agent-step', agentView.steps[i].status]"
                  >
                    <span class="num">{{ i+1 }}</span>
                    <code>{{ agentView.steps[i].line }}</code>
                  </li>
                </ol>
              </div>
              <pre v-else class="small muted">No steps found.</pre>

              <div v-if="agentView.artifacts?.length" class="shots">
                <div v-for="(a,k) in agentView.artifacts" :key="'shot-'+k" class="shot">
                  <img :src="a.url" :alt="a.label" />
                  <div class="cap">{{ a.label }}</div>
                </div>
              </div>

              <div v-if="browserPlan?.api">
                <p class="mb-1"><strong>API:</strong> {{ browserPlan.api }}</p>
                <pre class="small">{{ JSON.stringify(browserPlan.params || {}, null, 2) }}</pre>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="showPlanDialog = false">Close</v-btn>
              <v-btn color="deep-purple" :loading="loadingRun" @click="approveAndRunPlan">
                ▶️ Approve & Run
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>

      <v-card v-if="agentSearchQuery" class="mt-4 pa-3 neon" outlined>
        <div class="d-flex align-center justify-space-between">
          <div class="anim-heading">
            <v-icon left small>mdi-google</v-icon>
            Browser Search for: <strong>{{ agentSearchQuery }}</strong>
          </div>
          <div>
            <v-btn small
              :href="`https://www.google.com/search?q=${encodeURIComponent(agentSearchQuery)}`"
              target="_blank" rel="noopener"
            >Open in Google</v-btn>
          </div>
        </div>

        <div class="results-grid mt-3" v-if="googleResults.length">
          <div v-for="(r,i) in googleResults.slice(0,8)" :key="'ag-'+i" class="result-card">
            <div class="rcard-title" :title="r.title">{{ r.title }}</div>
            <div class="rcard-snippet">{{ r.snippet }}</div>
            <div class="rcard-meta">
              <v-chip x-small>{{ r.tag || 'Agent' }}</v-chip>
              <span class="host">{{ prettyUrl(r.link) }}</span>
            </div>
            <div class="rcard-actions">
              <v-btn x-small @click="openTab(r.link, 'page', r.title)">Open</v-btn>
              <a class="open-link" :href="r.link" target="_blank" rel="noopener">Visit</a>
              <v-btn x-small outlined color="primary" :disabled="isInSources(r.link)" @click="addSource(r)">
                Add to sources
              </v-btn>
            </div>
          </div>
        </div>
        <div class="muted small" v-else>Searching…</div>
      </v-card>

      <v-card v-if="agentView.artifacts && agentView.artifacts.length" class="mt-3 pa-3" outlined>
        <div class="d-flex align-center justify-space-between">
          <div class="anim-heading"><v-icon left small>mdi-camera</v-icon> Screenshots</div>
          <div class="muted small">from the last run</div>
        </div>
        <div class="shots mt-2">
          <div v-for="(a,k) in agentView.artifacts" :key="'shot-inline-'+k" class="shot">
            <img :src="a.url" :alt="a.label" style="max-width: 280px; border-radius: 10px;" />
            <div class="cap">{{ a.label }}</div>
          </div>
        </div>
      </v-card>

      <v-alert v-if="statusMsg" type="info" dense outlined class="mt-3">{{ statusMsg }}</v-alert>

      <v-alert v-if="response.gptMessage" ref="answerCard" type="success" outlined class="mt-4 text-left neon">
        <div class="d-flex align-center justify-space-between mb-2">
          <h4 class="mb-0">Answer</h4>
          <div>
            <v-btn small class="mr-2" @click="readOutLoud(response.gptMessage)" :disabled="!response.gptMessage">🔊 Speak</v-btn>
            <v-btn small outlined color="red" @click="stopSpeaking(true)">⏹ Stop Speaking</v-btn>
          </div>
        </div>
        <div v-html="response.gptMessage"></div>
      </v-alert>

      <v-card ref="researchConsole" class="research-console mt-4 neon" outlined>
        <div class="rc-header">
          <div class="rc-title"><v-icon left small>mdi-web</v-icon>Research Console</div>
          <div class="rc-actions">
            <v-btn small outlined @click="clearConsole" :disabled="loading">Clear</v-btn>
          </div>
        </div>
 <div class="screens-layout">
      <!-- Screen 1 -->
      <div class="screen screen-1">
        <div class="rc-tabs">
          <div
            v-for="t in tabs.filter(tab => (tab.screenId || 1) === 1)"
            :key="t.id"
            class="rc-tab"
            :class="{ active: t.id === activeTabId }"
            @click="activeTabId = t.id"
          >
            <span class="dot" :class="t.status"></span>
            <span class="label" :title="t.title || t.url">
              {{ t.title || prettyUrl(t.url) }}
            </span>
            <v-btn x-small icon @click.stop="closeTab(t.id)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Optional placeholder when no tabs -->
          <div v-if="!tabs.some(tab => (tab.screenId || 1) === 1)" class="rc-empty">
            No tabs on Screen 1 yet.
          </div>

          <div class="spacer"></div>
        </div>
      </div>

      <!-- Screen 2 -->
      <div class="screen screen-2">
        <div class="rc-tabs">
          <div
            v-for="t in tabs.filter(tab => tab.screenId === 2)"
            :key="t.id"
            class="rc-tab"
            :class="{ active: t.id === activeTabId }"
            @click="activeTabId = t.id"
          >
            <span class="dot" :class="t.status"></span>
            <span class="label" :title="t.title || t.url">
              {{ t.title || prettyUrl(t.url) }}
            </span>
            <v-btn x-small icon @click.stop="closeTab(t.id)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Optional placeholder when no tabs -->
          <div v-if="!tabs.some(tab => tab.screenId === 2)" class="rc-empty">
            No tabs on Screen 2 yet.
          </div>

          <div class="spacer"></div>
        </div>
      </div>
    </div>


        <div class="rc-tabs">
          <div
            v-for="t in tabs"
            :key="t.id"
            class="rc-tab"
            :class="{active: t.id === activeTabId}"
            @click="activeTabId = t.id"
          >
            <span class="dot" :class="t.status"></span>
            <span class="label" :title="t.title || t.url">{{ t.title || prettyUrl(t.url) }}</span>
            <v-btn x-small icon @click.stop="closeTab(t.id)"><v-icon small>mdi-close</v-icon></v-btn>
          </div>
          <div class="spacer"></div>
        </div>

        <div class="rc-body">
          <div class="rc-viewer" ref="viewerWrap" :class="{'step-running': agentView.steps?.some(s=>s.status==='running')}">
            <div v-if="useBrowserAgent && agentView.steps?.length" class="agent-overlay">
              <v-chip :color="agentView.statusColor" dark>{{ agentView.statusLabel }}</v-chip>
              <div class="current-step" v-if="agentView.steps.find(s=>s.status==='running')">
                <span class="idx">
                  Step {{ agentView.steps.findIndex(s=>s.status==='running') + 1 }}
                </span>
                <span class="line">{{ agentView.steps.find(s=>s.status==='running')?.line }}</span>
              </div>
              <div class="hint" v-else>Planned — awaiting run.</div>
            </div>

            <div class="rep-live-frame">
              <div class="rep-live-frame__chrome">
                <div class="rep-live-frame__traffic"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
                <div class="rep-live-frame__url">
                  <v-icon x-small left style="opacity:.8">mdi-lock</v-icon>
                  <span class="rep-live-frame__urltext">{{ prettyUrl(activeTab?.url || '') }}</span>
                </div>
              </div>

              <iframe
                v-if="activeTab"
                :key="activeTab.id"
                :src="activeTab.url"
                class="rep-live-frame__viewport"
                ref="viewer"
                sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
              ></iframe>
              <div v-else class="rep-live-frame__placeholder">Open a tab by asking a question or starting a search.</div>
            </div>
          </div>

          <div class="rc-side">
            <div class="side-section">
              <div class="side-title"><v-icon left small>mdi-google</v-icon>Google Results</div>
              <div v-if="googleResults.length" class="results-grid">
                <div v-for="(r,i) in googleResults" :key="'gr-'+i" class="result-card">
                  <div class="rcard-title" :title="r.title">{{ r.title }}</div>
                  <div class="rcard-snippet">{{ r.snippet }}</div>
                  <div class="rcard-meta">
                    <v-chip x-small>{{ r.tag || 'research' }}</v-chip>
                    <span class="host">{{ prettyUrl(r.link) }}</span>
                  </div>
                  <div class="rcard-actions">
                    <v-btn x-small @click="openTab(r.link, 'page', r.title)">Open</v-btn>
                    <a class="open-link" :href="r.link" target="_blank" rel="noopener">Visit</a>
                    <v-btn x-small color="primary" outlined :disabled="isInSources(r.link)" @click="addSource(r)">Add to sources</v-btn>
                  </div>
                </div>
              </div>
              <div v-else class="muted small">No results yet.</div>
            </div>

            <div class="side-section">
              <div class="side-title"><v-icon left small>mdi-database-import</v-icon>Sources</div>
              <div v-if="response.onlineResults.length" class="results-grid">
                <div v-for="(s, i) in response.onlineResults" :key="'src-'+i" class="result-card">
                  <div class="rcard-title" :title="s.title || prettyUrl(s.link)">{{ s.title || prettyUrl(s.link) }}</div>
                  <div class="rcard-snippet">{{ s.snippet }}</div>
                  <div class="rcard-meta">
                    <v-chip x-small>{{ s.tag }}</v-chip>
                    <span class="host">{{ prettyUrl(s.link) }}</span>
                  </div>
                  <div class="rcard-actions">
                    <v-btn x-small @click="openTab(s.link, 'page', s.title || '')">Open</v-btn>
                    <a class="open-link" :href="s.link" target="_blank" rel="noopener">Visit</a>
                  </div>
                </div>
              </div>
              <div v-else class="muted small">Nothing collected yet.</div>
            </div>

            <div class="side-section">
              <div class="side-title"><v-icon left small>mdi-history</v-icon>Timeline</div>
              <div class="timeline">
                <div v-for="(ev, i) in timeline" :key="'tl-'+i" class="tl-item">
                  <div class="tl-time">{{ ev.time }}</div>
                  <div class="tl-msg" v-html="ev.msg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card>

      <v-dialog v-model="reporterOpen" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card class="reporter-wrap">
          <div class="rep-header">
            <div class="rep-left">
              <div class="rep-breadcrumb">
                <v-chip x-small class="rep-chip">AI Reporter</v-chip>
                <span class="sep">/</span>
                <span class="rep-time">{{ reporter.meta.time }}</span>
              </div>
              <div class="rep-header-stats">
                <v-chip x-small class="hud-chip"><v-icon left x-small>mdi-file-document-outline</v-icon>{{ (reporter.facts||[]).length }} facts</v-chip>
                <v-chip x-small class="hud-chip"><v-icon left x-small>mdi-source-branch</v-icon>{{ (reporter.sources||[]).length }} sources</v-chip>
                <v-chip x-small class="hud-chip"><v-icon left x-small>mdi-timer-outline</v-icon>{{ reporterStats.readMins }} min read</v-chip>
              </div>
            </div>

            <div class="rep-right">
              <v-chip x-small :class="['onair', { live: reporterPlaying }]">
                <span class="dot"></span>
                <span class="lbl">{{ reporterPlaying ? 'ON AIR' : 'STANDBY' }}</span>
              </v-chip>

              <div class="rep-actions">
                <v-btn small :loading="reporterLoading" @click="refreshReporter"><v-icon left small>mdi-refresh</v-icon>Refresh</v-btn>
                <v-btn small class="ml-2"  @click="startReporterShow"><v-icon left small>mdi-play</v-icon>Play</v-btn>
                <v-btn small class="ml-1"  @click="pauseReporterShow"><v-icon left small>mdi-pause</v-icon>Pause</v-btn>
                <v-btn small class="ml-1" color="primary" @click="prevReporterItem"><v-icon left small>mdi-skip-previous</v-icon></v-btn>
                <v-btn small class="ml-1" color="primary" @click="nextReporterItem"><v-icon left small>mdi-skip-next</v-icon></v-btn>
                <v-btn small outlined color="red" class="ml-2" @click="stopReporter(true)"><v-icon left small>mdi-stop-circle</v-icon>Stop</v-btn>
                <v-btn small class="ml-2" color="primary" @click="openAllSources"><v-icon left small>mdi-open-in-new</v-icon>Open Sources</v-btn>
                <v-btn small class="ml-2" color="primary" @click="copyReporter"><v-icon left small>mdi-content-copy</v-icon>Copy</v-btn>
                <v-btn small class="ml-2" color="secondary" outlined @click="reporterOpen=false"><v-icon left small>mdi-close</v-icon>Close</v-btn>
              </div>

              <div class="rep-vox">
                <v-switch v-model="autoListen" inset dense class="mr-2" label="Auto-listen" :disabled="!voiceSupported" />
                <v-chip x-small :class="['vox-state', { on: voiceMode==='followup' && recognizing }]">
                  <v-icon left x-small>{{ recognizing ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
                  {{ voiceMode==='followup' && recognizing ? 'Listening for follow-up…' : 'Mic idle' }}
                </v-chip>
              </div>
            </div>

            <v-progress-linear
              v-if="reporterQueue && reporterQueue.length"
              class="rep-progress"
              :value="reporterProgress"
              height="3"
              color="deep-purple accent-2"
              rounded
            />
          </div>

          <v-card dark class="rep-followup mt-3" outlined>
            <div class="rep-followup-row">
              <v-text-field
                v-model="followup.prompt"
                label="Follow up"
                dense
                clearable
                placeholder="e.g., 'browse google.com show top 3' • 'check wikipedia' • or ask a question"
                @keydown.enter="handleFollowupAsk"
              />
              <v-switch v-model="followup.useLastTopic" inset class="ml-4" label="Use last topic" />
              <v-btn :loading="followup.running" color="primary" class="ml-2" @click="handleFollowupAsk">Ask</v-btn>
            </div>
            <div class="rep-followup-actions">

              <v-btn small outlined @click="doTopGoogle3()">Top 3 on Google</v-btn>
              <v-btn small outlined @click="doWikipediaCheck()">Check Wikipedia</v-btn>
              <v-btn small outlined @click="doQuickAnswer()">Quick Answer</v-btn>
<v-tooltip bottom>
  <template v-slot:activator="{ on }">
    <v-btn
      small
      class="ml-2"
      v-on="on"
      :color="recognizing && voiceMode==='followup' ? 'red darken-1' : 'teal lighten-1'"
      :disabled="!voiceSupported"
      @click="toggleFollowupMic"
    >
      <v-icon left small>
        {{ recognizing && voiceMode==='followup' ? 'mdi-stop-circle' : 'mdi-microphone' }}
      </v-icon>
      {{ recognizing && voiceMode==='followup' ? 'Stop' : 'Follow-Up' }}
    </v-btn>
  </template>
  <span>{{ recognizing && voiceMode==='followup' ? 'Stop listening' : 'Start follow-up mic' }}</span>
</v-tooltip>

            </div>
          </v-card>
<div class="rep-hero" :style="reporterHeroStyle">
  <div class="rep-hero-media">
    <div class="rep-live-frame">
      <img :src="reporterHeroUrl" alt="Source snapshot" />
    </div>

    <div class="rep-hero-thumbs" v-if="heroThumbs.length">
      <img
        v-for="(t,i) in heroThumbs"
        :key="'hthumb-'+i"
        :src="t"
        :alt="'thumb '+(i+1)"
        class="rep-hero-thumb"
        :class="{ active: t === reporterHeroUrl }"
        @click="setReporterHero(t)"
      />
    </div>
  </div>

  <div class="rep-hero-shade"></div>
  <div class="rep-hero-content">
    <div class="rep-kicker" v-if="reporter.meta.kicker">{{ reporter.meta.kicker }}</div>
    <h1 class="rep-title">{{ reporter.title || 'AI Report' }}</h1>
    <div class="rep-subtitle" v-if="reporter.subtitle">{{ reporter.subtitle }}</div>
    <div class="rep-byline">
      <v-icon left small>mdi-microphone</v-icon>
      Reported by <strong>Cogneetiv AI Assistant</strong>
      <span v-if="reporter.meta.location">• {{ reporter.meta.location }}</span>
    </div>

    <div class="rep-hero-hud">
      <v-chip small class="hud-tag">
        <v-icon left small>mdi-radar</v-icon>{{ voiceStage || 'idle' }}
      </v-chip>
      <v-chip small class="hud-tag" v-if="reporterQueue && reporterQueue.length">
        <v-icon left small>mdi-format-list-bulleted</v-icon>
        {{ reporterCursor + 1 }} / {{ reporterQueue.length }}
      </v-chip>
    </div>
  </div>
</div>

          <div class="rep-body">
            <v-container fluid>
              <v-layout row wrap>
                <v-flex xs12 md8 class="pr-md-6">
                  <v-card class="rep-lede-card glass" v-if="reporter.lede" outlined>
                    <div class="rep-lede" v-html="reporter.lede"></div>
                  </v-card>

                  <v-card class="rep-content glass mt-4 md-12" v-if="reporter.html" outlined>
                    <div class="rep-content-inner" v-html="reporter.html"></div>
                  </v-card>

                  <v-card v-if="reporter.answer" class="rep-answer glass mt-6" outlined>
                    <div class="rep-answer-head">
                      <v-icon left>mdi-comment-quote-outline</v-icon>
                      Assistant’s Answer
                    </div>
                    <div class="rep-answer-body" v-html="reporter.answer"></div>
                  </v-card>

                  <v-card class="rep-source-live mt-4" outlined>
                    <div class="rep-side-title"><v-icon left small>mdi-web</v-icon> Live Source</div>
                    <div class="rep-live-frame">
                      <div class="rep-live-frame__chrome">
                        <div class="rep-live-frame__traffic"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
                        <div class="rep-live-frame__url">
                          <v-icon x-small left style="opacity:.8">mdi-lock</v-icon>
                          <span class="rep-live-frame__urltext">{{ prettyUrl(activeTab?.url || '') }}</span>
                        </div>
                      </div>
                      <iframe
                        v-if="activeTab"
                        :key="activeTab.id"
                        :src="activeTab.url"
                        class="rep-live-frame__viewport"
                        ref="viewer"
                        sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
                      ></iframe>
                      <div v-else class="rep-live-frame__placeholder">No page selected</div>
                    </div>
                    <div class="rep-live-actions mt-2">
                      <v-btn x-small @click="activeTab && openTab(activeTab.url,'page')">Open</v-btn>
                      <a v-if="activeTab" class="open-link" :href="activeTab.url" target="_blank" rel="noopener">Visit</a>
                    </div>
                  </v-card>
                </v-flex>

                <v-flex xs12 md4>
                  <v-card class="rep-sources glass" outlined>
                    <div class="rep-side-title"><v-icon left small>mdi-source-branch</v-icon> Sources</div>
                    <div v-if="(reporter.sources || []).length" class="rep-source-list">
                      <div v-for="(s,i) in reporter.sources" :key="'src-'+i" class="rep-source-item">
                        <div class="src-title" :title="s.title">{{ s.title || prettyUrl(s.link) }}</div>
                        <div class="src-host">{{ prettyUrl(s.link) }}</div>
                        <div class="src-actions">
                          <v-btn x-small @click="openTab(s.link,'page',s.title||'')">Open</v-btn>
                          <a class="open-link" :href="s.link" target="_blank" rel="noopener">Visit</a>
                        </div>
                      </div>
                    </div>
                    <div v-else class="muted small pa-3">No sources yet.</div>
                  </v-card>

                  <v-card class="rep-facts glass mt-4" outlined v-if="(reporter.facts||[]).length">
                    <div class="rep-side-title"><v-icon left small>mdi-clipboard-text-outline</v-icon> Key Facts</div>
                    <ul class="rep-facts-list">
                      <li v-for="(f,i) in reporter.facts" :key="'fact-'+i">{{ f }}</li>
                    </ul>
                  </v-card>

                  <v-card class="rep-actions-card glass mt-4" outlined v-if="(reporter.nextSteps||[]).length">
                    <div class="rep-side-title"><v-icon left small>mdi-check-circle-outline</v-icon> Next Steps</div>
                    <ul class="rep-steps">
                      <li v-for="(n,i) in reporter.nextSteps" :key="'step-'+i">{{ n }}</li>
                    </ul>
                  </v-card>

                  <v-card class="rep-running glass mt-4" outlined>
                    <div class="rep-side-title"><v-icon left small>mdi-format-list-bulleted</v-icon> Running Order</div>
                    <div class="runlist" ref="runlist">
                      <div
                        v-for="(item, i) in reporterQueue"
                        :key="'q-'+i"
                        :class="['run-item', item.type, { active: i===reporterCursor, done: i<reporterCursor }]"
                        @click="jumpReporterItem(i)"
                      >
                        <span class="idx">{{ i+1 }}</span>
                        <span class="icon" v-html="item.icon"></span>
                        <span class="txt">{{ item.label }}</span>
                      </div>
                    </div>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-container>
          </div>

          <transition name="fade-up">
            <div class="rep-urlcard glass" v-if="activeUrlCard">
              <v-icon left small>mdi-link-variant</v-icon>
              <a :href="activeUrlCard" target="_blank" rel="noopener">{{ activeUrlCard }}</a>
            </div>
          </transition>

          <transition name="slide-up">
            <div class="rep-ticker" v-if="reporterPlaying">
              <div class="dot live"></div>
              <div class="ticker-text">{{ currentSpokenText }}</div>
            </div>
          </transition>

          <div class="rep-footer" v-if="reporterLoading">
            <v-progress-linear indeterminate/>
            <div class="muted small ml-2">Drafting report…</div>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </v-main>
</template>


<script>
import axios from "axios";
import jsPDF from "jspdf";

export default {
data() {
  return {
  viz: {
        raf: null,
        ctx: null,
        dpr: (typeof window !== "undefined" ? window.devicePixelRatio : 1) || 1,
        freqs: null,
        shuffle: [1, 3, 0, 4, 2],
        opts: {
          smoothing: 0.6,
          fft: 8,
          minDecibels: -70,
          fillOpacity: 0.6,
          lineWidth: 1,
          glow: 18,
          blend: "screen",
          shift: 42,
          width: 48,
          amp: 2.0,
          color1: [203, 36, 128],
          color2: [41, 200, 192],
          color3: [24, 137, 218],
        },
      },
sourceFocus: {       
  url: "", title: "", host: "", snippet: "", text: ""
},
followup: {
  prompt: "",
  useLastTopic: true,
  running: false,
},
screens: [1, 2],
    activeScreen: 1,
    tabs: [],
    activeTabId: null,

    targetScreenForThisRun: null,
      agentView: {
      status: "idle", // 'idle' | 'planning' | 'planned' | 'running' | 'completed' | 'error'
      statusLabel: "Idle",
      statusColor: "grey",
      steps: [],
      artifacts: [],
    },
lastResearchTopic: "",
      heroOverride: '',

   reporterFallback: require('../assets/starsbg.png'),
autoListen: true,
autoListenDelay: 450,
voiceMode: 'idle',
recentFollowups: [],
lastFollowupTS: 0,
followupRateMs: 1200,
autoOpenViewerOnFollowup: true,
reporterPlaying: false,
    reporterCursor: 0,
    reporterQueue: [],
    activeUrlCard: "",
reporterItemDelay: 350,
currentSpokenText: "",
       liveSource: {
      url: "",
      title: "",
      textPreview: ""
    },
      autoOpenSourceTabs: true,
      reporterOpen: false,
      reporterLoading: false,
      reporter: {
        title: "",
        subtitle: "",
        lede: "",
        html: "",
        answer: "",
        hero: "",
        sources: [],
        facts: [],
        nextSteps: [],
        meta: { time: "", location: "", kicker: "" }
      },
    ttsSpeaking: false,
    ttsBeat: 0,
    ttsPatternSeed: 0,
    ttsTempo: 2.0,
    ttsLastBoundaryTS: 0,
    mode: "general",
    loading: false,
    stage: "idle",
    statusMsg: "",
    error: { message: "" },
    searchPreview: [],
    knowledgeBase: [],
    timeline: [],
    showAnswering: false,
    answerBgTimer: null,
    useBrowserAgent: false,
    loadingPlan: false,
    loadingRun: false,
    showPlanDialog: false,
    browserPlan: { steps: [] },
browserRun: null,
agentSearchQuery: "", 
    googleResults: [],

    autoResearch: false,
    question: "",
    quickOpen: false,
    quickAction: {
      kind: "",
      fields: {
        query: "",
        url: "",
        count: 3,
        full: true,
        contains: "",
        name: "Instaguard Security",
        email: "contact@example.com",
        message: "Requesting a proposal for mobile patrol in Los Angeles.",
      },
    },
    voiceSupported: false,
    waveLevel: 0,
    recognizing: false,
    recognition: null,
voiceStage: 'idle', // 'idle' | 'listening' | 'processing' | 'reporting'
    interimTranscript: '',
    finalTranscript: '',
    voiceLang: "en-US",
    ttsReadFinalTranscript: true,
    stream: null,
    audioCtx: null,
    analyser: null,
    rafId: null,
    leadForm: { services: [], industries: [], location: "Los Angeles, CA", radius: 25, limit: 30 },
    serviceOptions: ["Unarmed Guard","Armed Guard","Mobile Patrol","Event Security","Alarm Response"],
    industryOptions: ["Apartments","Property Management","Construction","Retail","HOA","Event Venues","Nightclubs","Film Production","Hospitals","Schools","Logistics/Warehouse","Car Dealerships","Parking Lots"],

    competitorQuery: `security guard company Los Angeles armed patrol reviews license "PPO" -jobs`,
    response: { gptMessage: "", message: "", onlineResults: [] },
    serverBase: (process.env.VUE_APP_NODE_SERVER || "").trim().replace(/\/+$/, ""),
 gptApiKey: "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", // Replace with your OpenAI API key
      googleApiKey: "AIzaSyBboVjZMYbpCulM_NhAuW5I_e6ZwD5f8qA", // Replace with your Google API key
      searchEngineId: "87a5cd3086b3e4db7 ", // Replace with your Google Custom Search Engine ID
      cseCx:  "87a5cd3086b3e4db7",
    };
  },

  computed: {
     runStatusMessage() {
    return (this.browserRun && (this.browserRun.message || this.browserRun.error)) || "";
  },
     canShowResearchConsole() {
    return (this.tabs?.length > 0) ||
           (this.googleResults?.length > 0) ||
           (this.response?.onlineResults?.length > 0);
  },
    reporterProgress() {
  const total = (this.reporterQueue && this.reporterQueue.length) || 0;
  if (!total) return 0;
  const idx = Math.min(this.reporterCursor + 1, total);
  return Math.round((idx / total) * 100);
},
reporterStats() {
  const html = (this.reporter.lede || '') + ' ' + (this.reporter.html || '') + ' ' + (this.reporter.answer || '');
  const words = this.stripHtml(html).split(/\s+/).filter(Boolean).length;
  return { readMins: Math.max(1, Math.round(words / 180)) };
  
},
     quickTitle() {
      const map = {
        'search-open': 'Search the web & open results',
        'visit-screenshot': 'Visit URL & take a screenshot',
        'find-rfp': 'Find RFP PDFs',
        'check-text': 'Check that a page contains text',
        'fill-form': 'Fill a contact form (no submit)',
      };
      return map[this.quickAction?.kind] || 'Quick Task';
    },
     statusLabel() {
    if (this.status === 'planned') return 'Planned (waiting to run)';
    if (this.status === 'running') return 'Running...';
    if (this.status === 'done') return 'Completed';
    if (this.status === 'failed') return 'Failed';
    return 'Unknown';
  },
activeTab() {
      return this.tabs.find(t => t.id === this.activeTabId) || null;
    },

    reporterHeroUrl() {
      try {
        if (this.heroOverride) return this.heroOverride;

        const tab = this.activeTab;
        if (tab?.screenshot) return tab.screenshot;
        if (tab?.url) {
          const shot = this.buildSnapshotUrl(tab.url);
          if (shot) return shot;
        }

        if (this.reporter?.hero) return this.reporter.hero;

        const firstSrc = (this.reporter?.sources || [])[0];
        if (firstSrc?.link) {
          const shot = this.buildSnapshotUrl(firstSrc.link);
          if (shot) return shot;
        }

        return ''; // use gradient if no asset
      } catch (e) {
        console.warn('reporterHeroUrl error:', e);
        return '';
      }
    },
    reporterHeroStyle() {
      const img = this.reporterHeroUrl;
      return img ? `--hero:url('${img}')` : `--hero:none`;
    },
    heroThumbs() {
      const thumbs = [];
      try {
        if (this.agentView && Array.isArray(this.agentView.artifacts)) {
          for (const a of this.agentView.artifacts) {
            if (a?.url) thumbs.push(a.url);
          }
        }
        const srcs = this.reporter?.sources || [];
        for (const s of srcs) {
          if (s?.image) thumbs.push(s.image);
          else if (s?.link) {
            const u = this.buildSnapshotUrl(s.link);
            if (u) thumbs.push(u);
          }
        }
        if (this.reporter?.hero) thumbs.push(this.reporter.hero);
        const seen = new Set();
        return thumbs.filter(u => {
          const k = String(u || '').trim().toLowerCase();
          if (!k || seen.has(k)) return false;
          seen.add(k);
          return true;
        }).slice(0, 8);
      } catch (e) {
        console.warn('heroThumbs error:', e);
        return [];
      }
    },
    mergedResults() {
      const all = [...(this.response.onlineResults || []), ...(this.googleResults || [])];
      return this.uniqueByLink(all);
    },
//  agentView() {
//     const planSteps = Array.isArray(this.browserPlan?.steps) ? this.browserPlan.steps : [];
//     const steps = planSteps.map((s, i) => ({
//       idx: i,
//       line: this.formatStepLine(s),
//       status: 'pending',
//     }));

//     const logs = Array.isArray(this.browserRun?.log) ? this.browserRun.log : [];
//     const navTrail = logs.filter(e => e?.type === 'navurl' && e?.url).map(e => e.url);

//     for (const entry of logs) {
//       if (!entry || typeof entry.i !== 'number' || !steps[entry.i]) continue;
//       if (entry.type === 'step:start') steps[entry.i].status = 'running';
//       if (entry.type === 'step:done')  steps[entry.i].status = 'done';
//       if (entry.type === 'step:error') steps[entry.i].status = 'error';
//     }

//     const firstErrorIdx = logs.find(e => e?.type === 'step:error')?.i ?? -1;
//     if (firstErrorIdx < 0) {
//       const started = logs.filter(e => e?.type === 'step:start').map(e => e.i);
//       const dones   = new Set(logs.filter(e => e?.type === 'step:done').map(e => e.i));
//       const runningIdx = started.reverse().find(i => !dones.has(i));
//       if (typeof runningIdx === 'number' && steps[runningIdx] && steps[runningIdx].status !== 'done') {
//         steps[runningIdx].status = 'running';
//       }
//     }

//     const artifacts = logs
//       .filter(e => e?.type === 'artifact' && e?.kind === 'screenshot' && e?.path)
//       .map((e, k) => ({
//         url: this.serverAbsolute(String(e.path || '').replace('./','/')),
//         label: `shot ${k + 1}`,
//       }));

//     const hasSteps = steps.length > 0;
//     const hasRun = !!this.browserRun && (
//       (Array.isArray(this.browserRun.log) && this.browserRun.log.length > 0) ||
//       !!this.browserRun.finalUrl
//     );

//     let statusLabel = 'Idle';
//     let statusColor = 'grey';

//     const hasError = logs.some(e => e?.type === 'step:error');
//     if (!hasRun && hasSteps) {
//       statusLabel = 'Planned — awaiting run';
//       statusColor = 'blue';
//     } else if (hasRun && hasError) {
//       statusLabel = 'Failed';
//       statusColor = 'red';
//     } else if (hasRun && this.browserRun?.ok) {
//       statusLabel = 'Finished';
//       statusColor = 'green';
//     } else if (hasRun && !this.browserRun?.ok) {
//       statusLabel = 'In progress';
//       statusColor = 'orange';
//     }

//     const runningIdx = steps.findIndex(s => s.status === 'running');
//     const doneCount  = steps.filter(s => s.status === 'done').length;

//     return {
//       steps,
//       artifacts,
//       statusLabel,
//       statusColor,
//       hasSteps,
//       hasRun,
//       runningIdx,
//       runningLine: runningIdx >= 0 ? steps[runningIdx].line : '',
//       doneCount,
//       totalSteps: steps.length,
//       finalUrl: this.browserRun?.finalUrl || '',
//       runId: this.browserRun?.runId || '',
//       error: this.browserRun?.error || '',
//       navTrail,
//       SERVICES: [
//   { key: 'upcoming', label: 'Upcoming Dates', synonyms: ['upcoming', 'future', 'announced'] },
//   { key: 'past', label: 'Past Shows', synonyms: ['past', 'history', 'setlist'] },
//   { key: 'ticketmaster', label: 'Ticketmaster', synonyms: ['tm', 'tickets', 'on sale', 'presale'] },
//   { key: 'bandsintown', label: 'Bandsintown', synonyms: ['bit', 'b.i.t.'] },
//   { key: 'setlistfm', label: 'Setlist.fm', synonyms: ['setlist', 'sets'] },
//   { key: 'venues', label: 'Venues & Capacity', synonyms: ['capacity', 'arena', 'stadium', 'theatre'] },
//   { key: 'routing', label: 'Routing', synonyms: ['route', 'drive', 'flight'] },
//   { key: 'festivals', label: 'Festivals', synonyms: ['festival', 'lineup'] },
//   { key: 'news', label: 'News/Press', synonyms: ['announce', 'announcement', 'press'] },
//   { key: 'contacts', label: 'Contacts', synonyms: ['agent', 'manager', 'promoter', 'agency'] }
// ],
//     };
//   },
  },
  mounted() {
this.setupSpeech();
  window.addEventListener("keydown", this.globalHotkeys);
  this.drawWave();
  this.initVoiceViz();

  if ('speechSynthesis' in window) {
    const fire = () => { window.speechSynthesis.getVoices(); };
    fire();
    window.speechSynthesis.onvoiceschanged = fire;
  }
  },
  beforeDestroy() {
    this.cleanupAudio();
    window.removeEventListener("keydown", this.globalHotkeys);
    if (this.answerBgTimer) { clearTimeout(this.answerBgTimer); this.answerBgTimer = null; }

     if (this.viz && this.viz.raf) {
      cancelAnimationFrame(this.viz.raf);
      this.viz.raf = null;
    }
  },

  watch: {
      loading(v){ if (v) this.showAnswering = false; },
       recognizing(n) { if (!n && !this.loading) this.vizEaseToIdle(); },
    stage(val) { if (val === "idle" && !this.recognizing) this.vizEaseToIdle(); },
     finalTranscript(newVal) {
    if (!newVal) return;
    if (this.voiceMode === 'followup') {
      const text = String(newVal || '').trim();
      this.voiceMode = 'idle';
      this.stopListening(true);
      if (text) {
        this.followup.prompt = text;
        this.handleFollowupAsk();
      }
    }
  },
  activeTabId() {
  try {
    const tab = this.activeTab;
    if (tab?.url) {
      const shot = this.buildSnapshotUrl(tab.url);
      if (shot) this.heroOverride = shot;
    }
  } catch (err) {
    this.log && this.log('activeTab hero update failed: ' + (err?.message || err));
  }
}
  },

  methods: {
startListening() {
    if (this.voiceSupported && !this.recognizing) this.toggleVoice();
  },
  stopListening(force = false) {
    if (this.voiceSupported && (this.recognizing || force)) this.toggleVoice();
  },
shouldIgnoreDuplicateFollowup(text="") {
  const t = String(text).trim().toLowerCase();
  if (!t) return true;
  const now = Date.now();
  if (now - this.lastFollowupTS < this.followupRateMs) return true;
  this.lastFollowupTS = now;
  const dup = this.recentFollowups.find(x => x === t);
  this.recentFollowups = [t, ...this.recentFollowups].slice(0, 6);
  return !!dup;
},

classifyFollowup(raw="") {
  const text = String(raw || this.followup.prompt || "").trim();
  const urls = this.extractUrls ? this.extractUrls(text) : [];
  const norm = text.toLowerCase();

  if (urls.length) return { kind: "openUrl", url: urls[0], topic: this.baseSubject() };
  if (/\bwikipedia\b/.test(norm)) return { kind: "wikipedia", topic: this.baseSubject() || text };
  if (/\b(google|search|browse|look\s*up)\b/.test(norm) && /\btop\s*(3|three)\b/.test(norm))
    return { kind: "googleTopN", n: 3, topic: this.baseSubject() || text };
  if (/\b(compare|vs\.?|versus|pros and cons)\b/.test(norm))
    return { kind: "compareSources", topic: this.baseSubject() || text };
  if (/\b(summar(y|ise|ize)|what\s+is\s+on\s+this\s+page|tl;dr)\b/.test(norm))
    return { kind: "summarizeActive", topic: this.baseSubject() || text };

  return { kind: "quickAnswer", topic: this.baseSubject() || text };
},

async ensureReporterOpen() {
  if (!this.reporterOpen) this.reporterOpen = true;
  await this.$nextTick();
  if (this.autoOpenViewerOnFollowup) this.focusReporterLive();
},

ensureOpenUniqueTab(url, title="") {
  const u = String(url || "").trim();
  if (!u) return;
  const already = (this.tabs || []).some(t => (t.url || "").replace(/\/+$/, "") === u.replace(/\/+$/, ""));
  if (!already) this.openTab(u, "page", title || this.prettyUrl(u));
},

// Google results cache: only re-search if query changed
async ensureGoogleResults(topic) {
  const currentKey = String(this.agentSearchQuery || "").toLowerCase();
  const wantKey = String(topic || "").toLowerCase();
  if (currentKey === wantKey && this.googleResults?.length) return;
  this.agentSearchQuery = topic;
  const items = await this.cseSearch(topic, "FollowUp");
  this.googleResults = items || [];
},

// Speak & re-arm follow-up mic (no URL path reading)
async speakAndMaybeAutoListen(text="") {
  const safe = this.ttsSanitizeNarration ? this.ttsSanitizeNarration(text) : text;
  this.readOutLoud(safe);
  if (this.autoListen && this.voiceSupported) {
    setTimeout(() => { this.voiceMode = "followup"; this.startListening(); }, this.autoListenDelay || 450);
  }
},

// Append compact host-based reference chips to a follow-up block
appendReferences(htmlBlock = "", sources = []) {
  if (!sources?.length) return htmlBlock;
  const hosts = [];
  for (const s of sources.slice(0,6)) {
    const h = this.prettyUrl(s.link || "");
    if (h && !hosts.includes(h)) hosts.push(h);
  }
  if (!hosts.length) return htmlBlock;
  const refs = `<div class="muted tiny mt-2">Refs: ${hosts.map((h,i)=>`[${i+1}] ${this.escapeHtml(h)}`).join(" · ")}</div>`;
  return `${htmlBlock}${refs}`;
},
// Summarize the currently viewed page
async summarizeActiveTab(topicHint="") {
  const tab = this.activeTab;
  if (!tab?.url) {
    this.reporter.answer = (this.reporter.answer || '') + `<p>No page is open to summarize.</p>`;
    await this.speakAndMaybeAutoListen(`There's no page open to summarize.`);
    return;
  }
  try {
    const crawled = await this.crawlUrl(tab.url);
    const text = (crawled.text || "").slice(0, 6000); // trim large pages
    const sys = "Summarize the provided page text for a busy reader. Focus on key facts, definitions, claims, and any numbers. Keep to 5–8 bullets plus a 1–2 sentence overview.";
    const user = `TOPIC HINT: ${topicHint || this.lastResearchTopic || ""}\n\nTEXT:\n${text}`;
    const html = await this.chatComplete([{role:'system',content:sys},{role:'user',content:user}], null, 'gpt-4o');

    let block = `<h3>Page Summary</h3><div>${html}</div>`;
    block = this.appendReferences(block, [{link: tab.url}]);
    this.reporter.answer = (this.reporter.answer || '') + block;

    this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));
    await this.speakAndMaybeAutoListen(this.stripHtml(html));
    this.focusReporterLive();
  } catch (e) {
    const msg = e?.message || 'Could not summarize this page.';
    this.reporter.answer = (this.reporter.answer || '') + `<p>${this.escapeHtml(msg)}</p>`;
    await this.speakAndMaybeAutoListen(`I couldn't summarize this page.`);
  }
},
async compareTopSources(topic="") {
  let picks = (this.reporter.sources || []).slice(0, 3);
  if (!picks.length && this.googleResults?.length) picks = this.googleResults.slice(0,3);

  if (!picks.length) {
    await this.ensureGoogleResults(topic || this.lastResearchTopic || this.question || '');
    picks = this.googleResults.slice(0,3);
  }
  if (!picks.length) {
    this.reporter.answer += `<p>No sources found to compare.</p>`;
    await this.speakAndMaybeAutoListen(`No sources yet to compare.`);
    return;
  }
  const snapshots = [];
  for (const s of picks) {
    try {
      const c = await this.crawlUrl(s.link);
      snapshots.push({
        host: this.prettyUrl(s.link),
        title: s.title || this.prettyUrl(s.link),
        excerpt: (c.text || "").split(/\n/).slice(0,6).join(" ").slice(0, 600)
      });
    } catch (e) {
      snapshots.push({ host: this.prettyUrl(s.link), title: s.title || this.prettyUrl(s.link), excerpt: "" });
    }
  }

  const sys = `Compare sources succinctly. Output:
- One paragraph summary of agreement/disagreement.
- A table with columns: Source, Key Points, Caveats.
- 1–2 sentences on where to verify next.`;
  const user = `TOPIC: ${topic || this.lastResearchTopic || this.question}\n\nSOURCES:\n${snapshots.map((s,i)=>`[${i+1}] ${s.title} (${s.host})\n${s.excerpt}`).join("\n\n")}`;
  const html = await this.chatComplete([{role:'system',content:sys},{role:'user',content:user}], null, 'gpt-4o');

  let block = `<h3>Source Comparison</h3><div>${html}</div>`;
  block = this.appendReferences(block, picks);
  this.reporter.answer = (this.reporter.answer || '') + block;
  this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));
  await this.speakAndMaybeAutoListen(this.stripHtml(html));
},
async doQuickAnswerEnhanced(text = "") {
  const q = (text || "").trim();
  if (!q) return;

  const sys = this.buildSystemPrompt();
  const ctx = (this.reporter.sources || []).slice(0, 6)
    .map((s,i) => `- [${i+1}] ${s.title || this.prettyUrl(s.link)} — ${this.prettyUrl(s.link)}`)
    .join('\n');

  const user = `
FOLLOW-UP QUESTION:
${q}

RECENT CONTEXT (if relevant):
${ctx || '(none)'}

REPLY CONCISELY IN HTML (1–2 short paragraphs + optional 3 bullets). Avoid long URLs; use hostnames if linking.`.trim();

  try {
    const html = await this.chatComplete([{role:'system', content: sys}, {role:'user', content: user}], null, 'gpt-4o');
    let block = `<h3>Follow-up</h3><div>${html}</div>`;
    block = this.appendReferences(block, this.reporter.sources || []);
    this.reporter.answer = (this.reporter.answer || '') + block;
    this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));
    await this.speakAndMaybeAutoListen(this.stripHtml(html));
    if (this.activeTab || (this.tabs && this.tabs.length)) {
      this.focusReporterLive();
    }
  } catch (e) {
    const msg = e?.message || 'Unknown error';
    this.reporter.answer = (this.reporter.answer || '') + `<p>Couldn’t get a quick answer (${this.escapeHtml(msg)}).</p>`;
    await this.speakAndMaybeAutoListen(`I couldn't get a quick answer.`);
  }
},
  toggleFollowupVoice() {
    if (!this.voiceSupported) return;
    if (this.voiceMode === 'followup' && this.recognizing) {
      this.stopListening(true);
      this.voiceMode = 'idle';
    } else {
      this.voiceMode = 'followup';
      this.startListening();
    }
  },
  toggleFollowupMic() {
    this.toggleFollowupVoice();
  },
/**
 * Parse phrases like:
 *  - "move this tab to screen 2"
 *  - "send current tab to screen one"
 *  - "open this on screen two"
 *  - "search for Drake on screen 2"
 *
 * Returns:
 *  - null if nothing detected
 *  - { type: "moveActiveTab", screen: 1|2 }
 *  - { type: "openOnScreen",   screen: 1|2 }
 */
  parseScreenCommand(text) {
    if (!text) return null;
    const lower = text.toLowerCase();

    const match = lower.match(/\bscreen\s*(one|two|1|2)\b/);
    if (!match) return null;

    const token = match[1];
    const screen =
      token === "2" || token === "two" ? 2 : 1;

    if (
      /move (this|the current|current|active)?\s*tab/.test(lower) ||
      /send (this|the current|current|active)?\s*tab/.test(lower)
    ) {
      return { type: "moveActiveTab", screen };
    }

    if (/open (it|this|that)?\s*on\b/.test(lower) || /on screen/.test(lower)) {
      return { type: "openOnScreen", screen };
    }

    // Fallback: just treat as "open on screen" if screen appears
    return { type: "openOnScreen", screen };
  },

  async handleScreenVoiceCommand(rawText) {
    const cmd = this.parseScreenCommand(rawText);
    if (!cmd) return null;

    if (cmd.type === "moveActiveTab") {
      const tabId =
        this.activeTabId ||
        (this.tabs && this.tabs.length
          ? this.tabs[this.tabs.length - 1].id
          : null);

      if (!tabId) {
        this.readOutLoud?.("I don't have any tabs open to move yet.");
        return { handled: true };
      }

      this.moveTabToScreen(tabId, cmd.screen);
      this.readOutLoud?.(`Moving current tab to screen ${cmd.screen}.`);
      return { handled: true };
    }

    // Not a pure move, just a "open on screen X" hint
    return {
      handled: false,
      targetScreen: cmd.screen,
    };
  },

  stripScreenPhrases(text) {
    if (!text) return text;
    return text
      .replace(/\bon screen\s*(one|two|1|2)\b/gi, "")
      .trim();
  },

    async onVoiceFinal(transcript) {
    this.previewTranscript = "";
    const text = (transcript || "").trim();
    if (!text) return;

    // Example: if you're using runGeneral for research & tab opening
    this.question = text;         // used by runGeneral
    await this.runGeneral();      // this already handles screen voice commands
  },


  baseSubject() {
    if (this.followup.useLastTopic) {
      const t = (this.reporter?.title || "").trim();
      if (t) return t;
      if (this.lastResearchTopic) return this.lastResearchTopic;
    }
    const p = (this.followup.prompt || "").trim();
    if (!p) return this.lastResearchTopic || this.question || "";
    const cleaned = p
      .replace(/\b(browse|search|google|show|check|visit|open|look\s*up)\b/ig, "")
      .replace(/\b(top\s*\d+|top\s+three|top\s+3|results?)\b/ig, "")
      .replace(/\b(for|about|on|re|re:)\b/ig, " ")
      .replace(/\s+/g, " ")
      .trim();
    return cleaned || this.lastResearchTopic || this.question || "";
  },

  wantsWikipedia(text="") {
    return /\bwikipedia\b/i.test(String(text || this.followup.prompt));
  },
  wantsGoogleTop3(text="") {
    const s = String(text || this.followup.prompt);
    return /\b(google|search|browse)\b/i.test(s) && /\b(top\s*(3|three))\b/i.test(s);
  },
scrollInto(el) {
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (err) {
    try {
      const rectTop = el.getBoundingClientRect ? el.getBoundingClientRect().top : 0;
      const top = rectTop + (window.pageYOffset || document.documentElement.scrollTop || 0) - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    } catch (err2) {
      if (this.log) this.log(`scrollInto fallback failed: ${err2?.message || err2}`);
      else console.warn && console.warn('scrollInto fallback failed:', err2); // ok if no-console disabled
    }
  }
},
  flash(el) {
    if (!el) return;
    el.classList.add('focus-flash');
    setTimeout(() => el.classList.remove('focus-flash'), 1200);
  },
  focusAnswerArea() {
    const el = this.$refs.answerCard && (this.$refs.answerCard.$el || this.$refs.answerCard);
    if (el) { this.scrollInto(el); this.flash(el); }
  },
  focusResearchConsole() {
    const el = this.$refs.researchConsole && (this.$refs.researchConsole.$el || this.$refs.researchConsole);
    if (el) { this.scrollInto(el); this.flash(el); }
  },
  focusViewerArea() {
    const wrap = this.$refs.viewerWrap && (this.$refs.viewerWrap.$el || this.$refs.viewerWrap);
    if (wrap) { this.scrollInto(wrap); this.flash(wrap); }
  },
  focusReporterLive() {
    this.$nextTick(() => {
      const el = this.$el.querySelector('.rep-live-frame');
      if (el) { this.scrollInto(el); this.flash(el); }
    });
  },
  async handleFollowupAsk() {
    const text = (this.followup.prompt || '').trim();
    if (!text) return;
    try {
      this.followup.running = true;
      await this.processFollowup(text);
    } finally {
      this.followup.running = false;
      this.followup.prompt = '';
    }
  },
async processFollowup(prompt) {
  if (this.shouldIgnoreDuplicateFollowup(prompt)) return;

  const intent = this.classifyFollowup(prompt);
  await this.ensureReporterOpen();

  switch (intent.kind) {
    case "openUrl": {
      this.ensureOpenUniqueTab(intent.url, this.prettyUrl(intent.url));
      this.reporter.sources = this.uniqueByLink([...(this.reporter.sources || []), { title: this.prettyUrl(intent.url), link: intent.url }]);
      await this.speakAndMaybeAutoListen(`Opening ${this.prettyUrl(intent.url)}.`);
      this.focusReporterLive();
      break;
    }
    case "googleTopN": {
      await this.ensureGoogleResults(intent.topic);
      const n = intent.n || 3;
      const top = (this.googleResults || []).slice(0, n);
      top.forEach(r => this.ensureOpenUniqueTab(r.link, r.title || this.prettyUrl(r.link)));
      let block = `<h3>Top ${n} on Google</h3><ol>` +
        top.map(r => `<li><a href="${r.link}" target="_blank" rel="noopener">${this.escapeHtml(r.title || this.prettyUrl(r.link))}</a> <span class="muted">(${this.prettyUrl(r.link)})</span></li>`).join("") +
        `</ol>`;
      block = this.appendReferences(block, top);
      this.reporter.answer = (this.reporter.answer || '') + block;
      this.focusReporterLive();
      await this.speakAndMaybeAutoListen(`Loaded top ${n} results from Google.`);
      break;
    }
    case "wikipedia": {
      await this.doWikipediaCheck(intent.topic);
      break;
    }
    case "summarizeActive": {
      await this.summarizeActiveTab(intent.topic);
      break;
    }
    case "compareSources": {
      await this.compareTopSources(intent.topic);
      break;
    }
    default: {
      await this.doQuickAnswerEnhanced(prompt);
      break;
    }
  }
},
async handleFollowupSpeech(text = "") {
  const t = String(text || "").trim();
  if (!t) return;

  const urls = this.extractUrls(t);
  const wantsTop3 = /\b(top\s*(3|three))\b/i.test(t) && /\b(google|search|browse|look\s*up)\b/i.test(t);
  const wantsWiki = /\bwikipedia\b/i.test(t);
  if (urls.length) {
    const u = urls[0];
    this.openTab(u, 'page', this.prettyUrl(u));
    this.reporter.sources = this.uniqueByLink([...(this.reporter.sources || []), { title: this.prettyUrl(u), link: u }]);
    this.readOutLoud(`Opening ${this.prettyUrl(u)}.`);
    return;
  }

  if (wantsTop3) {
    await this.doTopGoogle3(t);
    return;
  }

  if (wantsWiki) {
    await this.doWikipediaCheck(t);
    return;
  }
  await this.doQuickAnswer(t);
},
buildSnapshotUrl(url) {
      try {
        if (!url) return '';
        const clean = String(url).trim();
        if (/^(about:|data:|chrome:)/i.test(clean)) return '';
        const norm = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
        return `https://s0.wp.com/mshots/v1/${encodeURIComponent(norm)}?w=1600`;

      } catch (e) {
        console.warn('buildSnapshotUrl error:', e);
        return '';
      }
    },
async doTopGoogle3(text = "") {
  const stripped = text.replace(/\b(top\s*(3|three)|on|from|in|google|search|results?)\b/gi, '').trim();
  const topic = stripped || this.lastResearchTopic || this.question || '';
  if (!topic) return;

  this.log(`🔎 Follow-up: Google top 3 for <b>${this.escapeHtml(topic)}</b>`);
  const items = await this.cseSearch(topic, 'FollowUp');
  const top3 = items.slice(0, 3);

  top3.forEach(r => this.openTab(r.link, 'page', r.title || this.prettyUrl(r.link)));

  const html =
    `<h3>Top 3 (Google)</h3><ol>` +
    top3.map(r => `<li><a href="${r.link}" target="_blank" rel="noopener">${this.escapeHtml(r.title || this.prettyUrl(r.link))}</a> <span class="muted">(${this.prettyUrl(r.link)})</span></li>`).join('') +
    `</ol>`;

  this.reporter.answer = (this.reporter.answer || '') + html;
await this.speakAndMaybeAutoListen("Top three results loaded from Google.");
this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));

  this.focusReporterLive();
},

async doWikipediaCheck(text = "") {
  const topic = text.replace(/\bwikipedia\b/gi, '').trim() || this.lastResearchTopic || this.question || '';
  if (!topic) return;

  const query = `site:wikipedia.org ${topic}`;
  this.log(`🗂️ Follow-up: Wikipedia for <b>${this.escapeHtml(topic)}</b>`);
  const items = await this.cseSearch(query, 'Wikipedia');
  if (!items.length) {
    this.reporter.answer = (this.reporter.answer || '') + `<p>No Wikipedia page found for <em>${this.escapeHtml(topic)}</em>.</p>`;
    await this.speakAndMaybeAutoListen("No Wikipedia page found.");
    return;
  }
  this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));

  const best = items[0];
  this.openTab(best.link, 'page', best.title || 'Wikipedia');
  try {
    const crawled = await this.crawlUrl(best.link);
    const firstPara = (crawled.text || '').split(/\n{2,}/)[0].trim().slice(0, 600);
    const html =
      `<h3>Wikipedia</h3>` +
      `<p><a href="${best.link}" target="_blank" rel="noopener">${this.escapeHtml(best.title || this.prettyUrl(best.link))}</a> — ${this.escapeHtml(this.prettyUrl(best.link))}</p>` +
      (firstPara ? `<p>${this.escapeHtml(firstPara)}</p>` : '');
    this.reporter.answer = (this.reporter.answer || '') + html;
    await this.speakAndMaybeAutoListen("Pulled a summary from Wikipedia.");
    this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));

  } catch (e) {
    const html =
      `<h3>Wikipedia</h3><p><a href="${best.link}" target="_blank" rel="noopener">${this.escapeHtml(best.title || this.prettyUrl(best.link))}</a></p>`;
    this.reporter.answer = (this.reporter.answer || '') + html;
await this.speakAndMaybeAutoListen("Opened the Wikipedia page.");
this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));

  }
},

async doQuickAnswer(text = "") {
  const q = (text || "").trim();
  if (!q) return;

  const sys = this.buildSystemPrompt();
  const ctx = (this.reporter.sources || []).slice(0, 6)
    .map((s,i) => `- [${i+1}] ${s.title || this.prettyUrl(s.link)} — ${this.prettyUrl(s.link)}`)
    .join('\n');

  const user = `
FOLLOW-UP QUESTION:
${q}

RECENT CONTEXT (if relevant):
${ctx || '(none)'}

REPLY CONCISELY IN HTML (1-2 short paragraphs, optional list). Include links only if needed.`.trim();

  try {
    const html = await this.chatComplete([{role:'system', content: sys}, {role:'user', content: user}], null, 'gpt-4o');
    const block = `<h3>Follow-up</h3><div>${html}</div>`;
    this.reporter.answer = (this.reporter.answer || '').replace(/(<h3>Follow-up<\/h3>[\s\S]*?)?$/,'') + block;
    await this.speakAndMaybeAutoListen("Follow-up question loaded.");
    this.$nextTick(() => {
      this.scrollIntoViewSafe(this.$el?.querySelector?.('.rep-answer'));
      setTimeout(() => this.scrollIntoViewSafe(this.$el?.querySelector?.('.rep-source-live')), 300);
    });
  } catch (e) {
    const msg = e?.message || 'Unknown error';
    this.reporter.answer = (this.reporter.answer || '') + `<p>Couldn’t get a quick answer (${this.escapeHtml(msg)}).</p>`;
    await this.speakAndMaybeAutoListen("I couldn't get a quick answer.");
    this.$nextTick(() => this.scrollInto(this.$el?.querySelector?.('.rep-answer')));
  }
},

ttsSanitizeNarration(text = "") {
    const toHost = (m) => this.prettyUrl ? this.prettyUrl(m) : (new URL(m).host || m);
    const urlRe = /\bhttps?:\/\/[^\s<>"')]+/gi;
    let out = String(text || "").replace(urlRe, toHost);
    const bareRe = /\b(?:[a-z0-9-]+\.)+(?:com|net|org|io|ai|app|co|dev|edu|gov|info|biz|news|tv|fm)\b(?:\/[^\s<>"')]*)?/gi;
    out = out.replace(bareRe, (m) => {
      try { return (new URL(/^https?:\/\//i.test(m) ? m : `https://${m}`)).host; }
      catch { return m; }
    });

    return out;
  },
  // prettyUrl(u = "") {
  //   try { return (new URL(u)).host.replace(/^www\./, ""); } catch { return u; }
  // },
async quickSynthesizeAnswer(question, items = [], label = "Update") {
  const sys = `
You are a crisp analyst. Using ONLY the provided items (titles/snippets/hosts), produce:
- A one-paragraph answer (2–4 sentences).
- 3 short bullets with the most relevant facts.
No extra links. No speculation. If insufficient context, say what to check next.`.trim();

  const ctx = items.slice(0, 6).map((r,i) => {
    const host = this.prettyUrl(r.link || "");
    return `[${i+1}] ${r.title || host} — ${host}\n${(r.snippet||"").slice(0, 300)}`;
  }).join("\n\n");

  const user = `QUESTION:\n${question}\n\nITEMS:\n${ctx || "(none)"}`.trim();

  try {
    const html = await this.chatComplete([{role:"system",content:sys},{role:"user",content:user}], null, "gpt-4o");
    const block = `<h3>${this.escapeHtml(label)}</h3><div>${html}</div>`;
    this.reporter.answer = (this.reporter.answer || "") + block;
    this.speakReporter?.();

    this.$nextTick(() => {
      this.scrollIntoViewSafe(this.$el?.querySelector?.('.rep-answer'));
      setTimeout(() => this.scrollIntoViewSafe(this.$el?.querySelector?.('#researchConsole')), 300);
    });
  } catch (e) {
    this.log && this.log(`Quick synthesis error: ${e?.message || e}`);
  }
},

detectResearchIntent(text = "") {
  const q = String(text || "").trim();
  if (!q) return { isResearch: false, urls: [] };

  const urls = this.extractUrls(q);
  const hasVerb = /\b(research|browse|investigate|scan|analy[sz]e|look\s*up|open|visit|check\s*out|go\s*to|review)\b/i.test(q);
  const hasSearchCue = /\b(search|google|find)\b/i.test(q);
  const hasBareDomain = /\b[a-z0-9-]+\.(?:com|net|org|io|ai|app|co|dev|edu|gov|info|biz|news|tv|fm)(?:[^\s<>"']*)?/i.test(q);

  return { isResearch: hasVerb || hasSearchCue || urls.length > 0 || hasBareDomain, urls };
},

extractUrls(text = "") {
  const out = [];
  const re = /((?:https?:\/\/)?(?:www\.)?[a-z0-9.-]+\.(?:com|net|org|io|ai|app|co|dev|edu|gov|info|biz|news|tv|fm)(?:[^\s<>"']*)?)/gi;
  let m; 
  while ((m = re.exec(text))) {
    let u = m[1];
    if (!/^https?:\/\//i.test(u)) u = "https://" + u;
    out.push(u);
  }
  return out;
},


escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
},
    _nextTtsPattern() {
  this.ttsPatternSeed = (this.ttsPatternSeed + 0.61803398875) % 1;
},
_ttsPhase(i) {
  if (!this._ttsSeeds || !this._ttsSeeds.length) {
    this._ttsSeeds = Array.from({ length: 8 }, () => Math.random() * Math.PI * 2);
  }
  return this._ttsSeeds[i % this._ttsSeeds.length];
},
  qa(kind) {
      return !!(this.quickAction && this.quickAction.kind === kind);
    },
    coerceQuickCount() {
      const n = parseInt(this.quickAction.fields.count, 10);
      this.quickAction.fields.count = Math.max(1, Math.min(5, isNaN(n) ? 3 : n));
    },
    openQuickAction(kind) {
      this.quickAction = {
        kind,
        fields: {
          query: '',
          url: '',
          count: 3,
          full: true,
          contains: '',
          name: 'Instaguard Security',
          email: 'contact@example.com',
          message: 'Requesting a proposal for mobile patrol in Los Angeles.',
        },
      };
      this.quickOpen = true;
    },
buildPromptFromQuick() {
  const k = this.quickAction.kind;
  const f = this.quickAction.fields;
  if (k === 'search-open') {
    return `Search Google for "${f.query}", open the first ${f.count} organic result(s), then take a full-page screenshot on the first opened page.`;
  }
  if (k === 'visit-screenshot') {
    return `Go to ${f.url}, wait for the main content, then take a ${f.full ? 'full-page' : 'viewport'} screenshot.`;
  }
  if (k === 'find-rfp') {
    return `Search Google for "${f.query} filetype:pdf", open the first PDF result, and take a full-page screenshot of page 1.`;
  }
  if (k === 'check-text') {
    return `Visit ${f.url}, confirm the text "${f.contains}" is visible on the page; if so, take a screenshot.`;
  }
  if (k === 'fill-form') {
    return `Open ${f.url}, fill the contact form with name "${f.name}", email "${f.email}", and message "${f.message}". Do not submit; take a screenshot of the filled form.`;
  }
  return '';
},
async spotlightSource(url, title = "") {
    if (!url) return;
    this.activeUrlCard = url;
    const id = this.openTab(url, "page", title || "Source");
    this.activeTabId = id;

    try {
      const crawled = await this.crawlUrl(url);
      this.liveSource = {
        url,
        title: crawled.title || this.prettyUrl(url),
        textPreview: (crawled.text || "").slice(0, 1800) // keep it light
      };
    } catch (err) {
      console.warn("Source preview fetch failed:", err);
      this.liveSource = { url, title: title || this.prettyUrl(url), textPreview: "" };
    }
  },
      setReporterHero(url) {
      this.heroOverride = url || '';
    },
async warmupSources(sources = []) {
  try {
    if (!Array.isArray(sources) || !sources.length) return;
    const first = sources[0];
    if (!this.tabs?.some(t => t.url === first.link)) {
      this.openTab(first.link, 'page', first.title || 'Source');
    }
    const found = (this.tabs || []).find(t => t.url === first.link);
    if (found) this.activeTabId = found.id;

  } catch (e) {
    console.warn('warmupSources failed:', e);
  }
},

async revealAndOpenSourceFromSegment(segment) {
  const s = String(segment || "");
  const m = s.match(/https?:\/\/[^\s)]+/i);
  if (m && m[0]) await this.spotlightSource(m[0]);
},


  async runReporterFromFinalTranscript() {
    const q = String(this.finalTranscript || this.question || '').trim();
    if (!q) return;
    this.voiceStage = 'processing';
    await this.runReporter(q, { forceOpen: true, speak: true });
    this.voiceStage = 'reporting';
  },
    async refreshReporter() {
      const base = String(this.finalTranscript || this.question || "").trim();
      if (!base) return;
      await this.runReporter(base, { forceOpen: true, speak: false });
    },

buildReporterQueue() {
  const Q = [];

  const push = (type, label, text, extra = {}) => {
    const icons = {
      title: '📰',
      lede: '✍️',
      section: '🧭',
      fact: '✅',
      answer: '💡',
      url: '🔗'
    };
    Q.push({
      type,
      label,
      text,
      icon: icons[type] || '•',
      ...extra
    });
  };

  if (this.reporter.title)   push('title',   'Title',   this.stripHtml(this.reporter.title));
  if (this.reporter.subtitle)push('section', 'Subtitle',this.stripHtml(this.reporter.subtitle));
  if (this.reporter.lede)    push('lede',    'Lede',    this.stripHtml(this.reporter.lede));

  (this.reporter.sources || []).forEach((s, i) => {
    const host = this.prettyUrl(s.link);
    push('url', `Source ${i+1} – ${host}`, `Source ${i+1}: ${s.title || host}. URL: ${s.link}`, { url: s.link });
  });

  (this.reporter.facts || []).forEach((f, i) => {
    push('fact', `Fact ${i+1}`, f);
  });

  if (this.reporter.html) push('section', 'Report Body', this.stripHtml(this.reporter.html));

  if (this.reporter.answer) push('answer', 'Answer', this.stripHtml(this.reporter.answer));

  if (this.reporter.nextSteps && this.reporter.nextSteps.length) {
    const joined = this.reporter.nextSteps.map((n, i) => `${i+1}. ${n}`).join('  ');
    push('section', 'Next Steps', `Recommended next steps: ${joined}`);
  }

  this.reporterQueue = Q;
  this.reporterCursor = 0;
},

ttsSpeak(text) {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) {
      resolve(); // fail-soft
      return;
    }
    const clean = this.stripHtml(text || '');
    if (!clean) { resolve(); return; }

    try { window.speechSynthesis.cancel(); } catch (e) { console.warn('tts cancel failed:', e); }

    const u = new SpeechSynthesisUtterance(clean);
    const v = this.getVoiceByName('Google UK English Female');
    if (v) u.voice = v;
    u.volume = 1; u.rate = 1; u.pitch = 1;

    this.ttsSpeaking = true;
    this.currentSpokenText = clean;

    u.onend = () => {
      this.ttsSpeaking = false;
      resolve();
    };
    u.onerror = () => {
      this.ttsSpeaking = false;
      resolve();
    };

    window.speechSynthesis.speak(u);
  });
},

async startReporterShow() {
  if (!this.reporterQueue.length) this.buildReporterQueue();
  this.reporterPlaying = true;
  this.voiceStage = 'reporting';
  await this.playFrom(this.reporterCursor);
const item = this.reporterQueue[this.reporterCursor];
if (item?.url) await this.spotlightSource(item.url);

},
pauseReporterShow() {
  this.reporterPlaying = false;
  try { window.speechSynthesis.cancel(); } catch (e) { console.warn('pause cancel failed:', e); }
},
stopReporter(resumeListening = false) {
  this.reporterPlaying = false;
  this.activeUrlCard = "";
  this.currentSpokenText = "";
  try { window.speechSynthesis.cancel(); } catch (e) { console.warn('stop cancel failed:', e); }
  this.ttsSpeaking = false;
  if (resumeListening) this.startVoice(); else this.voiceStage = this.recognizing ? 'listening' : 'idle';
},
prevReporterItem() {
  if (this.reporterCursor > 0) this.reporterCursor -= 1;
  if (this.reporterPlaying) this.playFrom(this.reporterCursor);
},
nextReporterItem() {
  if (this.reporterCursor < this.reporterQueue.length - 1) this.reporterCursor += 1;
  if (this.reporterPlaying) this.playFrom(this.reporterCursor);
},
jumpReporterItem(i) {
  this.reporterCursor = Math.max(0, Math.min(i, this.reporterQueue.length - 1));
  if (this.reporterPlaying) this.playFrom(this.reporterCursor);
},

scrollRunlistIntoView() {
  this.$nextTick(() => {
    const el = this.$refs.runlist?.querySelector('.run-item.active');
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });
},

async playFrom(index = 0) {
  if (!this.reporterPlaying) return;
  for (let i = index; i < this.reporterQueue.length; i++) {
    if (!this.reporterPlaying) break;
    this.reporterCursor = i;
    const item = this.reporterQueue[i];

    if (item.type === 'url' && item.url) {
      this.activeUrlCard = item.url;
      if (this.autoOpenSourceTabs) this.openTab(item.url, 'page', item.label || 'Source');
      await this.sleep(300);
    } else {
      this.activeUrlCard = "";
    }

    this.scrollRunlistIntoView();
    await this.ttsSpeak(item.text);

    await this.sleep(160);
  }

  this.reporterPlaying = false;
  this.activeUrlCard = "";
  this.currentSpokenText = "";
},
speakReporter() {
  const html = (this.reporter.lede || '') + '\n' + (this.reporter.html || '');
  const text = this.sanitizeForSpeech(html);   // 👈 use sanitized text
  if (!text) return;
  this.readOutLoud(text);
},
stopSpeaking(resumeListening = false) {
  if ('speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      // safely ignore speechSynthesis cancel errors
    }
  }
  this.currentUtterance = null;
  this.ttsSpeaking = false;
  if (resumeListening) {
    this.startVoice();
  } else {
    this.voiceStage = this.recognizing ? 'listening' : 'idle';
  }
},


    copyReporter() {
      const text =
        (this.reporter.title ? `# ${this.reporter.title}\n\n` : "") +
        (this.reporter.subtitle ? `_${this.reporter.subtitle}_\n\n` : "") +
        (this.stripHtml(this.reporter.lede || "") + "\n\n") +
        (this.stripHtml(this.reporter.html || "") + "\n\n") +
        (this.reporter.sources && this.reporter.sources.length
          ? "Sources:\n" +
            this.reporter.sources.map(s => `- ${s.title || this.prettyUrl(s.link)} — ${s.link}`).join("\n")
          : "");
      try {
        navigator.clipboard.writeText(text);
        this.$emit("toast", "Reporter content copied.");
      } catch (_) {
        this.$emit("toast", "Failed to copy reporter content.");
      }
    },
    openAllSources() {
      const list = (this.reporter.sources || []).slice(0, 6);
      list.forEach(s => this.openTab(s.link, "page", s.title || ""));
    },
async runReporter(rawQuery, opts = {}) {
  if (this.reporterLoading) return; // prevent parallel runs
  const query = (rawQuery || "").trim();
  this.lastResearchTopic = query;

  const forceOpen = !!opts.forceOpen;
  const speak = opts.speak !== false; // default true

  this.reporterLoading = true;
  if (forceOpen) this.reporterOpen = true;

  try {
    let sources = [];
    try { sources = await this.cseSearch(query, "Reporter"); }
    catch (e) { this.log && this.log(`Reporter CSE error: ${e?.message || e}`); sources = []; }

    let hero = "";
    const firstShot = this.agentView?.artifacts?.[0]?.url || "";
    if (firstShot) hero = firstShot;
    else if (sources[0]?.image) hero = sources[0].image;
    else if (sources[0]?.link) {
      try {
        const crawled = await this.crawlUrl(sources[0].link);
        const og = this.extractOgImage(crawled.text);
        hero = og || this.faviconFor(sources[0].link);
      } catch { hero = this.faviconFor(sources[0].link); }
    }

    if (sources[0]?.link && !this.tabs?.some(t => t.url === sources[0].link)) {
      this.openTab(sources[0].link, "page", sources[0].title || "Source");
    }

    const ctxBullets = sources.slice(0, 6).map((s, i) => {
      const host = this.prettyUrl(s.link);
      return `- [${i + 1}] ${s.title || host} — ${host}`;
    }).join("\n");

    const sys = `
You are an AI newsroom reporter. Produce a crisp, accurate report with a title, subtitle, short lede, clean HTML body (h2/p/ul/ol/blockquote only), explicit answer section, 4–8 bullet key facts, and 3–6 actionable next steps. Keep it concise but insightful. Do NOT invent sources; rely only on provided context. If uncertain, say so clearly. No inline CSS; semantic HTML only.
`.trim();

    const user = `
QUERY:
${query}

CONTEXT SOURCES (titles & hosts only):
${ctxBullets || "(none)"}

OUTPUT JSON (no prose outside JSON):
{
  "title": "...",
  "subtitle": "...",
  "lede_html": "<p>...</p>",
  "body_html": "<h2>...</h2><p>...</p>",
  "answer_html": "<p>...</p>",
  "facts": ["...", "..."],
  "next_steps": ["...", "..."],
  "kicker": "short section label like BREAKING / ANALYSIS / UPDATE",
  "where": "Location if deducible else empty"
}
`.trim();

    let json = {};
    try {
      const raw = await this.chatComplete(
        [{ role: "system", content: sys }, { role: "user", content: user }],
        null,
        "gpt-4o"
      );
      json = this.safeParseJSON(raw);
    } catch (e) {
      this.log && this.log(`Reporter GPT error: ${e?.message || e}`);
      json = {};
    }
    const now = new Date();
    this.reporter = {
      title: json.title || this.capFirst(this.truncate(query, 120)),
      subtitle: json.subtitle || "",
      lede: json.lede_html || "",
      html: json.body_html || "",
      answer: json.answer_html || "",
      hero,
      sources: this.uniqueByLink(sources.map(s => ({ title: s.title, link: this.ensureHttp(s.link) }))),
      facts: Array.isArray(json.facts) ? json.facts.slice(0, 10) : [],
      nextSteps: Array.isArray(json.next_steps) ? json.next_steps.slice(0, 10) : [],
      meta: { time: now.toLocaleString(), location: json.where || "", kicker: json.kicker || "UPDATE" },
    };
    await this.warmupSources(this.reporter.sources);
    try {
      if (this.reporter?.sources?.length) {
        this.reporter.sources = this.reporter.sources.map(s => ({ ...s, image: this.buildSnapshotUrl(s.link) }));
      }
    } catch (e) { console.warn('Snapshot enrich failed:', e); }
    this.$nextTick(() => {
      const host = this.$el;
      if (!host) return;
      const handle = (href, text) => this.spotlightSource(href, text);
      this.delegateLinks(host.querySelector('.rep-content'), handle);
      this.delegateLinks(host.querySelector('.rep-lede'), handle);
      this.delegateLinks(host.querySelector('.rep-answer-body'), handle);
    });
    this.lastResearchTopic = query;
    this.reporterPlaying = false;
    this.reporterCursor = 0;
    this.activeUrlCard = "";
    this.currentSpokenText = "";
    this.buildReporterQueue && this.buildReporterQueue();
    this.reporterOpen = true;
    this.voiceStage = "reporting";
    if (speak && this.startReporterShow) this.startReporterShow();
    this.$nextTick(() => {
      const dock = this.$el?.querySelector?.('.rep-followup input');
      dock?.focus?.();
    });
  } finally {
    this.reporterLoading = false;
  }
},

    extractOgImage(text="") {
      const m1 = text.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i);
      if (m1 && m1[1]) return m1[1];
      const m2 = text.match(/<meta[^>]+name=["']twitter:image["'][^>]+content=["']([^"']+)["']/i);
      if (m2 && m2[1]) return m2[1];
      return "";
    },
    faviconFor(url="") {
      try {
        const u = new URL(url);
        return `${u.origin}/favicon.ico`;
      } catch (_) { return ""; }
    },
    safeParseJSON(s="") {
      try {
        const start = s.indexOf("{");
        const end = s.lastIndexOf("}");
        if (start >= 0 && end > start) {
          const sub = s.slice(start, end + 1);
          return JSON.parse(sub);
        }
        return {};
      } catch (_) { return {}; }
    },
async callBrowserPlanner(approve = false) {
  const base = this.serverBase;
  if (!base) throw new Error("Missing VUE_APP_NODE_SERVER");

  const url = this.joinUrl(base, "/gpt/complete-sentence12");
  const alt = this.joinUrl(base, "/gpt/compelete-sentence12");

  const request = async (u) => {
    const resp = await fetch(u, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ message: this.question || "", approve }),
    });
    if (!resp.ok) throw new Error(`Server ${resp.status}: ${await resp.text()}`);
    return resp.json();
  };

  try {
    return await request(url);
  } catch {
    return await request(alt);
  }
},


async submitQuickAction(approve) {
  const msg = this.buildPromptFromQuick().trim();
  if (!msg) { this.$nextTick(()=>alert('Please complete required fields.')); return; }
  this.question = msg;
  this.quickOpen = false;

  await this.planBrowser(approve);
},
joinUrl(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return b && p ? `${b}/${p}` : (b || `/${p}`);
},
serverAbsolute(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return this.joinUrl(this.serverBase, p);
},
normalizeUrl(u) {
  try { const x = new URL(u); x.hash = ""; return x.toString(); } catch { return String(u || ""); }
},
    triggerAnsweringBg(ms = 5000) {

  this.showAnswering = true;
  if (this.answerBgTimer) clearTimeout(this.answerBgTimer);
  this.answerBgTimer = setTimeout(() => {
    this.showAnswering = false;
    this.answerBgTimer = null;
  }, ms);
},
getVoiceByName(name = 'Google UK English Female') {
  const voices = window.speechSynthesis ? window.speechSynthesis.getVoices() : [];
  const exact = voices.find(v => v.name === name);
  if (exact) return exact;

  const enGB = voices.find(v => /en-GB/i.test(v.lang));
  if (enGB) return enGB;

  return voices.find(v => /en-/i.test(v.lang)) || voices[0] || null;
},

stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = String(html);
  return (div.textContent || div.innerText || "").trim();
},

formatStepLine(s = {}) {
  const a = s.action;
  if (a === 'goto')       return `goto → ${s.url || '(missing url)'}`;
  if (a === 'waitFor')    return `waitFor → ${s.selector || '(missing selector)'} (state=${s.state || 'visible'}, timeout=${s.timeout || 20000}ms)`;
  if (a === 'click')      return `click → ${s.selector || '(missing selector)'} (timeout=${s.timeout || 15000}ms)`;
  if (a === 'type')       return `type → ${s.selector || '(missing selector)'} : "${(s.text || '').slice(0,120)}" (timeout=${s.timeout || 15000}ms)`;
  if (a === 'press')      return `press → ${s.selector || '(missing selector)'} [${s.key || 'Enter'}] (timeout=${s.timeout || 15000}ms)`;
  if (a === 'waitURL')    return `waitURL → ${s.urlPattern || '(missing pattern)'} (timeout=${s.timeout || 20000}ms)`;
  if (a === 'eval')       return `eval → ${typeof s.js === 'string' ? 'js(...)' : '(missing js)'}`
  if (a === 'screenshot') return `screenshot → ${s.full ? 'fullPage' : 'viewport'}`;
  return `action:${a || '(unknown)'}`;
},

async planBrowser(approve = false) {
  const q = (this.question || "").trim();
  if (!q) return alert("Enter a question first.");

  this.browserPlan = null;
  this.browserRun  = null;           // null (not an empty object) so "planned" state shows
  this.agentSearchQuery = "";        // clear previous query

  this.loadingPlan = !approve;
  this.loadingRun  = !!approve;

  try {
    this.log(approve
      ? "Contacting server to plan & run browser steps…"
      : "Contacting server to plan browser steps…");

    const data = await this.callBrowserPlanner(approve);

    if (data?.searchQuery && typeof data.searchQuery === "string" && data.searchQuery.trim()) {
      this.agentSearchQuery = data.searchQuery.trim();

      const tabId = this.openTab(
        `https://www.google.com/search?q=${encodeURIComponent(this.agentSearchQuery)}`,
        "google",
        "Agent Search"
      );

      try {
        await this.cseSearch(this.agentSearchQuery, "Agent");
        this.setTabStatus(tabId, "done");
      } catch (e) {
        this.setTabStatus(tabId, "error");
        this.log(`CSE failed: ${e?.message || e}`);
      }
    }

    if (data?.answer) this.log(data.answer);

    this.browserPlan = (data?.plan && Array.isArray(data.plan.steps))
      ? data.plan
      : (data?.plan || { steps: [] });

    const executed = !!data?.executed || !!approve;
    this.browserRun = executed && data?.run
      ? {
          ok: !!data.run.ok,
          finalUrl: data.run.finalUrl || "",
          log: Array.isArray(data.run.log) ? data.run.log : [],
          runId: data.run.runId || "",
          error: data.run.error || ""
        }
      : null;

    if (this.browserPlan?.steps?.length) {
      this.log(`Browser plan generated with <b>${this.browserPlan.steps.length}</b> step(s).`);
    } else if (this.browserPlan?.api) {
      this.log(`Plan requests API call: <b>${this.browserPlan.api}</b>.`);
    } else {
      this.log("No browser plan steps returned.");
    }

    if (executed && this.browserRun) {
      await this.consumeBrowserRun(this.browserRun);
    } else if (!approve && this.browserPlan) {
      this.showPlanDialog = true;
      try {
        await this.mirrorBrowserSearchToConsole(this.browserPlan); // extracts query from steps if present
      } catch (e) {
        this.log(`Mirror search failed: ${e?.message || e}`);
      }
    }
  } catch (e) {
    const msg = e?.message || "Unknown error";
    this.log(`Browser planning failed: ${msg}`);
    alert(`Browser planning failed: ${msg}`);
  } finally {
    this.loadingPlan = false;
    this.loadingRun  = false;
  }
},
parseQueryFromPlan(plan = {}) {
  if (!plan || !Array.isArray(plan.steps)) return null;

  const typeStep = plan.steps.find(s =>
    s.action === 'type' &&
    typeof s.selector === 'string' &&
    /input|textarea/i.test(s.selector) &&
    typeof s.text === 'string' &&
    s.text.trim().length > 0
  );
  if (typeStep) return typeStep.text.trim();

const goto = plan.steps.find(
  s => s.action === 'goto' && typeof s.url === 'string' && /google\.[^/]+\/search/i.test(s.url)
);
if (goto) {
  try {
    const u = new URL(goto.url);
    const q = u.searchParams.get('q');
    if (q) return q.trim();
  } catch (e) {
    const m = /[?&]q=([^&]+)/.exec(String(goto.url));
    if (m && m[1]) {
      try { return decodeURIComponent(m[1]).trim(); }
      catch { return m[1]; }
    }
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      console.warn('parseQueryFromPlan: failed to parse query from URL', goto.url, e);
    }
  }
}

  return null;
},

async mirrorBrowserSearchToConsole(plan = {}) {
  const q = this.parseQueryFromPlan(plan);
  if (!q) return;

  this.stage = "search";
  this.statusMsg = "Browser agent planning a search…";
  this.log(`Browser agent query: <b>${this.truncate(q, 160)}</b>`);

  const tabId = this.openTab(
    `https://www.google.com/search?q=${encodeURIComponent(q)}`,
    "google",
    "Agent Search"
  );

  try {
    const items = await this.cseSearch(q, "BrowserAgent");
    this.setTabStatus(tabId, "done");
    if (!items.length) this.log("No Google CSE results for agent query.");
  } catch (e) {
    this.log(`CSE failed for agent query: ${e.message}`);
    this.setTabStatus(tabId, "error");
  } finally {
    this.statusMsg = "";
  }
},

async approveAndRunPlan() {
  this.showPlanDialog = false;
  this.loadingRun = true;
  this.agentSearchQuery = "";
  this.browserRun  = null;

  try {
    this.log("Contacting server to plan & run browser steps…");

    const data = await this.callBrowserPlanner(true);

    if (data?.searchQuery && typeof data.searchQuery === "string" && data.searchQuery.trim()) {
      this.agentSearchQuery = data.searchQuery.trim();

      const tabId = this.openTab(
        `https://www.google.com/search?q=${encodeURIComponent(this.agentSearchQuery)}`,
        "google",
        "Agent Search"
      );

      try {
        await this.cseSearch(this.agentSearchQuery, "Agent");
        this.setTabStatus(tabId, "done");
      } catch (e) {
        this.setTabStatus(tabId, "error");
        this.log(`CSE failed: ${e?.message || e}`);
      }
    }

    this.browserPlan =
      (data?.plan && Array.isArray(data.plan.steps))
        ? data.plan
        : (data?.plan || { steps: [] });

    const executed = !!data?.executed || !!data?.run;
    this.browserRun = executed && data?.run
      ? {
          ok: !!data.run.ok,
          finalUrl: data.run.finalUrl || "",
          log: Array.isArray(data.run.log) ? data.run.log : [],
          runId: data.run.runId || "",
          error: data.run.error || ""
        }
      : null;

    if (data?.answer) this.log(data.answer);

    if (this.browserPlan?.steps?.length) {
      this.log(`Browser plan generated with <b>${this.browserPlan.steps.length}</b> step(s).`);
    } else if (this.browserPlan?.api) {
      this.log(`Plan requests API call: <b>${this.browserPlan.api}</b>.`);
    } else {
      this.log("No browser plan steps returned.");
    }

    if (this.browserRun) {
      await this.consumeBrowserRun(this.browserRun);
    } else {
      if (this.browserPlan?.steps?.length) {
        this.showPlanDialog = true;
      }
    }
  } catch (e) {
    const msg = e?.message || "Unknown error";
    this.log(`Run failed: ${msg}`);
    alert(`Run failed: ${msg}`);
  } finally {
    this.loadingRun = false;
  }
},


// 📝 Consume the run log + artifacts and open the final URL in your console
async consumeBrowserRun(run = null) {
  if (!run) return;

  if (run.ok) {
    this.log(`✅ Browser run <b>${run.runId}</b> finished. Final URL: <b>${this.prettyUrl(run.finalUrl || '')}</b>`);
    if (run.finalUrl) this.openTab(run.finalUrl, 'page', 'Final URL');
  } else {
    this.log(`❌ Browser run failed: ${run.error || 'Unknown error'}`);
  }

  const entries = Array.isArray(run.log) ? run.log : [];
entries.forEach(ev => {
  if (ev.type === 'step:start') {
    this.log(`▶️ Step ${ev.i+1}: <code>${this.formatStepLine(ev.s || {})}</code>`);
  } else if (ev.type === 'step:done') {
    this.log(`✔️ Step ${ev.i+1} done`);
  } else if (ev.type === 'artifact' && ev.kind === 'screenshot' && ev.path) {
    const url = ev.path.replace('./','/');
    this.log(`🖼️ Screenshot: <a href="${url}" target="_blank" rel="noopener">${url}</a>`);
  } else if (ev.type === 'consent:accepted') {
    this.log('✅ Consent dialog accepted automatically');
  }
});
  const hasShots = entries.some(e => e?.type === 'artifact' && e?.kind === 'screenshot' && e?.path);
  if (hasShots) {
    const shotsUrl = this.serverAbsolute('/shots');
    this.openTab(shotsUrl, 'shots', 'Screenshots');
  }
},

  sanitizeForSpeech(input = "") {
    const div = document.createElement("div");
    div.innerHTML = String(input);
    div.querySelectorAll("script,style,noscript").forEach(n => n.remove());

    div.querySelectorAll("a[href]").forEach(a => {
      const href = a.getAttribute("href") || "";
      let spoken = "";
      try {
        const u = new URL(href, window.location.origin);
        spoken = this.domainForSpeech(u.hostname);
      } catch {
        const m = href.match(/([a-z0-9.-]+\.[a-z]{2,})(?=\/|$)/i);
        spoken = m ? this.domainForSpeech(m[1]) : (a.textContent || "link");
      }
            a.replaceWith(document.createTextNode(`link ${spoken}`));

    });

    let out = (div.textContent || div.innerText || "").trim();

    out = out.replace(/https?:\/\/[^\s)]+|www\.[^\s)]+/gi, (m) => {
      try {
        const u = new URL(m.startsWith("http") ? m : ("http://" + m));
        return this.domainForSpeech(u.hostname);
      } catch {
        const mm = m.match(/([a-z0-9.-]+\.[a-z]{2,})(?=\/|$)/i);
        return mm ? this.domainForSpeech(mm[1]) : "";
      }
    });
    out = out.replace(/(^|\s)@([a-z0-9_]{2,})\b/gi, (_m, p1, user) => `${p1}at ${user}`);

    return out.replace(/\s{2,}/g, " ").trim();
  },
  domainForSpeech(host = "") {
    host = String(host).replace(/^www\./i, "").replace(/^m\./i, "").toLowerCase();
    const twoPartSuffixes = new Set([
      "co.uk","org.uk","gov.uk","ac.uk",
      "com.au","net.au","org.au",
      "co.nz","com.br","com.mx","com.tr","com.sg","co.in","com.cn","com.hk"
    ]);
    const parts = host.split(".");
    if (parts.length > 2) {
      const suffix2 = parts.slice(-2).join(".");
      if (twoPartSuffixes.has(suffix2) && parts.length >= 3) {
        return parts.slice(-3).join("."); // e.g., bbc.co.uk
      }
      return parts.slice(-2).join(".");   // e.g., nytimes.com
    }
    return host;
  },

readOutLoud(msg) {
  if (!('speechSynthesis' in window)) return;

  const sanitizeForTts = (html="") => {
    const text = this.stripHtml(html || "");
    return text.replace(/https?:\/\/([a-z0-9.-]+\.[a-z]{2,})(?:\/[^\s]*)?/gi, (_, host) => host);
  };

  const text = sanitizeForTts(msg);
  if (!text) return;

  const wasRecognizing = this.recognizing;
  if (wasRecognizing) this.stopVoice(); // avoid feedback

  const utter = new SpeechSynthesisUtterance(text);
  const voice = this.getVoiceByName('Google UK English Female');
  if (voice) utter.voice = voice;
  utter.volume = 1; utter.rate = 1; utter.pitch = 1;

  try { window.speechSynthesis.cancel(); } catch (err) { console.warn('speechSynthesis.cancel failed:', err); }

  this.ttsSpeaking = true;
  this.ttsBeat = 0.5; this.ttsTempo = 2.0; this._ttsLastBoundary = performance.now();

  utter.onstart = () => {
    this.ttsBeat = Math.min(0.8, this.ttsBeat + 0.2);
    this._ttsSeeds = Array.from({ length: 8 }, () => Math.random() * Math.PI * 2);
  };

  utter.onboundary = () => {
    this.ttsBeat = Math.min(0.9, this.ttsBeat + 0.4);
    const now = performance.now();
    const dt = now - (this._ttsLastBoundary || now);
    this._ttsLastBoundary = now;
    if (dt > 40 && dt < 1200) {
      const inst = 1000 / dt;
      this.ttsTempo = this.ttsTempo * 0.85 + inst * 0.15;
    }
  };

  const cleanupTTS = () => {
    this.ttsSpeaking = false;
    this.ttsBeat = 0;
    delete this._ttsLastBoundary;
  };

  utter.onend = () => {
    cleanupTTS();
    if (this.autoListen) this.startFollowupListening();
    else if (wasRecognizing) setTimeout(() => this.startSpeaking(), 150);
  };
  utter.onerror = (e) => { console.warn('TTS error:', e?.error || e); cleanupTTS(); };

  this.currentUtterance = utter;
  window.speechSynthesis.speak(utter);
},

async startFollowupListening() {
  if (!this.voiceSupported || !this.recognition) return;
  await new Promise(r => setTimeout(r, this.autoListenDelay));
  this.voiceMode = 'followup';
  try {
    await this.startWaveInput();
    if (this.recognizing) this.stopVoice();
    await this.startVoice(); // sets recognizing + chips
  } catch (e) {
    console.warn('startFollowupListening failed:', e?.message || e);
    this.voiceMode = 'normal';
  }
},

async startSpeaking() {
  try {
    this.showAnswering = false;

    if (this.voiceSupported && this.recognition) {
      await this.startWaveInput();
      await this.unlockAudioOnMobileGesture();
      if (!this.recognizing) {
        await this.startVoice();
      } else {
        this.stopVoice();
        await this.startVoice();
      }
      return;
    }

    await this.startWaveInput();
    this.recognizing = true;
    this.statusMsg = "Speech recognition not supported; mic visualizer active.";
  } catch (err) {
    this.statusMsg = "Couldn’t start microphone.";
  }
},
       initVoiceViz() {
      const cvs = this.$refs.vizCanvas;
      if (!cvs) return;
      const ctx = cvs.getContext("2d");
      this.viz.ctx = ctx;

      const resize = () => {
        const dpr = this.viz.dpr;
        const pxW = Math.min(1000, Math.floor(cvs.parentElement?.clientWidth || 760));
        const pxH = 150; // visualizer height
        cvs.width = pxW * dpr;
        cvs.height = pxH * dpr;
        cvs.style.width = pxW + "px";
        cvs.style.height = pxH + "px";
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      window.addEventListener("resize", resize);

      const loop = () => {
        this.vizRender();
        this.viz.raf = requestAnimationFrame(loop);
      };
      loop();
    },
    isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
},

async unlockAudioOnMobileGesture() {
  const resume = async () => {
    try {
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        await this.audioCtx.resume();
      }
    } catch (err) {
      console.warn('AudioContext resume failed or not needed:', err);
    }
  };
  if (this.audioCtx && this.audioCtx.state === 'running') return;
  await resume();

  if (this.audioCtx && this.audioCtx.state !== 'running') {
    await new Promise((resolve) => {
      const once = async () => {
        await resume();
        document.removeEventListener('touchend', once, true);
        document.removeEventListener('click', once, true);
        resolve();
      };
      document.addEventListener('touchend', once, true);
      document.addEventListener('click', once, true);
    });
  }
},
    vizUpdateAnalyserSettings() {
      const a = this.analyser;
      const o = this.viz.opts;
      if (!a) return;
      a.smoothingTimeConstant = o.smoothing;
      a.fftSize = Math.pow(2, o.fft);
      a.minDecibels = o.minDecibels;
      a.maxDecibels = 0;
      if (!this.viz.freqs || this.viz.freqs.length !== a.frequencyBinCount) {
        this.viz.freqs = new Uint8Array(a.frequencyBinCount);
      }
    },

vizRender() {
  const cvs = this.$refs.vizCanvas;
  const ctx  = this.viz.ctx;
  if (!cvs || !ctx) return;

  const W = cvs.width / this.viz.dpr;
  const H = cvs.height / this.viz.dpr;
  ctx.clearRect(0, 0, W, H);
  this.vizUpdateAnalyserSettings();

  const mid = H / 2;
  const o   = this.viz.opts;

  const speaking = !!this.analyser && !!this.recognizing; // user mic
  const tts      = !!this.ttsSpeaking;                    // AI reading

  let eTarget = 0;
  if (speaking) {
    const freqs = this.viz.freqs;
    this.analyser.getByteFrequencyData(freqs);
    let sum = 0, count = 0;
    const start = 2, end = Math.min(120, freqs.length); // focus low→mid
    for (let i = start; i < end; i++) { sum += freqs[i]; count++; }
    const avg = (sum / Math.max(1, count)) / 255;
    eTarget = Math.min(1, Math.pow(avg, 0.55) * 1.35);  // sensitive at lows + soft cap
  } else if (tts) {
    const now = performance.now();
    const tempo = Math.max(0.6, Math.min(3.5, this.ttsTempo || 2.0));
    eTarget = 0.12
            + 0.08 * Math.sin(now / (360 / (0.8 + 0.2 * tempo)))
            + 0.04 * Math.sin(now / (130 / (0.7 + 0.3 * tempo)) + Math.sin(now / 520));
    if (this.ttsBeat > 0) { eTarget += this.ttsBeat; this.ttsBeat *= 0.90; }
  } else {
    eTarget = 0.092 + 0.080 * Math.sin(performance.now() / 1400);
  }

  if (this.viz._e == null) this.viz._e = 0;
  let alpha = speaking ? 0.22 : (tts ? 0.18 : 0.10);
  const jump = Math.abs(eTarget - this.viz._e);
  if (jump > 0.22) alpha = Math.min(0.34, alpha + 0.09); // a hint of snap for big changes
  let energy = this.viz._e = this.viz._e * (1 - alpha) + eTarget * alpha;
  energy = Math.max(0, Math.min(1, energy));

  const tune = speaking ? {
    pxBase: 45, ampMul: 1.10, speedMain: 1.35, speedSub: 1.90,
    thickBase: 0.7, thickGain: 0.7, glowBase: 12
  } : tts ? {
    pxBase: 38, ampMul: 1.15, speedMain: 1.45, speedSub: 2.60,
    thickBase: 0.9, thickGain: 1.0, glowBase: 10
  } : {
    pxBase: 50, ampMul: 0.5, speedMain: 0.55, speedSub: 0.75,
    thickBase: 1.1, thickGain: 0.5, glowBase: 5
  };

  const lineWidth = (o.lineWidth || 1) * (speaking ? (1.2 + 0.9*energy)
                                     : tts      ? (1.0 + 0.8*energy + 0.4*(this.ttsBeat || 0))
                                                : (0.8 + 0.2*energy));
  const glow      = (o.glow || 0) + tune.glowBase + energy * 18;

  const toRGBA = (arr, a=1) => `rgba(${arr[0]|0},${arr[1]|0},${arr[2]|0},${a})`;
  const range  = (n) => Array.from({length:n}, (_,i)=>i);

  const pickFreq = (channel, i) => {
    if (!this.viz.freqs) return 0;
    const band = 3 * channel + this.viz.shuffle[i] * 6; // decorrelate bands
    const idx  = Math.min(this.viz.freqs.length - 1, band);
    return this.viz.freqs[idx] || 0;
  };

  const peakScale = (i) => {
    const x = Math.abs(2 - i);  // 2,1,0,1,2
    const s = 3 - x;            // 1,2,3,2,1
    return (s / 2) * (tune.ampMul * (0.55 + 0.65 * energy));
  };

  const _ttsPhase = (i) => {
    if (!this._ttsSeeds || !this._ttsSeeds.length) {
      this._ttsSeeds = Array.from({ length: 8 }, () => Math.random() * Math.PI * 2);
    }
    return this._ttsSeeds[i % this._ttsSeeds.length];
  };

  const now = performance.now();
  const tempoBoost = tts ? (0.4 + 0.2 * Math.min(2.0, this.ttsTempo || 2.0)) : 1;
  const tFast = now / (speaking ? (360 / tune.speedMain) : tts ? (260 / (tune.speedMain * tempoBoost)) : (1100 / tune.speedMain));
  const tSlow = now / (speaking ? (520 / tune.speedSub)  : tts ? (360 / (tune.speedSub  * tempoBoost)) : (1500 / tune.speedSub));

  const drawPath = (channel) => {
    const color = o[`color${channel + 1}`] || [255,255,255]; // <-- keep your palette
    ctx.fillStyle   = toRGBA(color, o.fillOpacity != null ? o.fillOpacity : 0.6);
    ctx.strokeStyle = ctx.shadowColor = toRGBA(color, 1);
    ctx.lineWidth   = lineWidth;
    ctx.shadowBlur  = glow;
    ctx.globalCompositeOperation = o.blend || 'screen';

    const points = 15, peaks = 5;
    const offset = (W - points * o.width) / 2;

    const x = range(points).map(i => offset + channel * o.shift + i * o.width);
    const y = range(peaks).map(i => {
      if (speaking) {
        const f = pickFreq(channel, i) / 255; // 0..1
        const px = tune.pxBase * (0.35 + 0.65 * f);
        const amp = peakScale(i) * px;
        return Math.max(0, mid - amp);
      } else if (tts) {
        const phase = _ttsPhase(i);
        const f1 = 0.5 + 0.5 * Math.sin(tFast + i*0.7 + phase);
        const f2 = 0.85 + 0.15 * Math.sin(tSlow + i + phase * 0.6);
        const f  = f1 * f2;
        const beatBoost = 1 + Math.min(0.7, (this.ttsBeat || 0) * 0.8);
        const px = tune.pxBase * (0.33 + 0.67 * f);
        const amp = peakScale(i) * px * beatBoost;
        return Math.max(0, mid - amp);
      } else {
        const f = 0.5 + 0.5 * Math.sin(tFast + i*0.6);
        const px = tune.pxBase * (0.30 + 0.40 * f);
        const amp = peakScale(i) * px;
        return Math.max(0, mid - amp);
      }
    });

    const h = 2 * mid;

    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(x[0], mid + 0.8);

    ctx.bezierCurveTo(x[1], mid + 0.5, x[2], y[0], x[3], y[0]);
    ctx.bezierCurveTo(x[4], y[0],     x[4], y[1], x[5], y[1]);
    ctx.bezierCurveTo(x[6], y[1],     x[6], y[2], x[7], y[2]);
    ctx.bezierCurveTo(x[8], y[2],     x[8], y[3], x[9], y[3]);
    ctx.bezierCurveTo(x[10], y[3],    x[10], y[4], x[11], y[4]);

    ctx.bezierCurveTo(x[12], y[4], x[12], mid, x[13], mid);
    ctx.lineTo(W, mid + 0.5);
    ctx.lineTo(x[13], mid - 0.5);

    ctx.bezierCurveTo(x[12], mid,   x[12], h - y[4], x[11], h - y[4]);
    ctx.bezierCurveTo(x[10], h - y[4], x[10], h - y[3], x[9], h - y[3]);
    ctx.bezierCurveTo(x[8],  h - y[3], x[8],  h - y[2], x[7], h - y[2]);
    ctx.bezierCurveTo(x[6],  h - y[2], x[6],  h - y[1], x[5], h - y[1]);
    ctx.bezierCurveTo(x[4],  h - y[1], x[4],  h - y[0], x[3], h - y[0]);
    ctx.bezierCurveTo(x[2],  h - y[0], x[1], mid,       x[0], mid);

    ctx.lineTo(0, mid);
    ctx.fill();
    ctx.stroke();
  };

  ctx.save();
  drawPath(0);
  drawPath(1);
  drawPath(2);
  ctx.restore();
},

resetMeters() {
  this.stopWaveInput();
  this.waveLevel = 0;
  const ring = this.$refs.voiceRing;
  if (ring) {
    ring.style.transform = 'scale(.9)';
    ring.style.opacity = '0.35';
  }
  this.vizClear();
},
vizClear() {
  const cvs = this.$refs.vizCanvas;
  const ctx = this.viz && this.viz.ctx;
  const dpr = (this.viz && this.viz.dpr) || (window.devicePixelRatio || 1);
  if (!cvs || !ctx) return;

  const W = cvs.width / dpr;
  const H = cvs.height / dpr;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, cvs.width, cvs.height);
  ctx.restore();

  ctx.clearRect(0, 0, W, H);
},

    isInSources(link = "") {
      const norm = this.normalizeUrl(link);
      return (this.response.onlineResults || []).some(r => this.normalizeUrl(r.link) === norm);
    },
setupSpeech() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  this.voiceSupported = !!SR;
  if (!SR) return;

  const rec = new SR();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = this.voiceLang;

  rec.onresult = (e) => {
    let interim = '';
    let finalsFound = false;

    for (let i = e.resultIndex; i < e.results.length; i++) {
      const seg = e.results[i][0].transcript || '';
      if (e.results[i].isFinal) {
        finalsFound = true;
        this._finalBuffer = (this._finalBuffer + ' ' + seg).trim();
      } else {
        interim += seg;
      }
    }

    this.interimTranscript = interim.trim(); // visual only

    if (finalsFound) {
      clearTimeout(this._finalDebounceId);
      this._finalDebounceId = setTimeout(async () => {
        const finalText = (this._finalBuffer || '').trim();
        this.finalTranscript = finalText;
        this.interimTranscript = '';
        this._finalBuffer = '';

        this.voiceStage = 'processing';
        this.stopVoice();

        try {
          if (this.reporterOpen && this.voiceMode === 'followup') {
            await this.handleFollowupSpeech(finalText);  // 🔊 follow-up routing
            this.voiceStage = 'reporting';
            if (this.autoListen) this.startFollowupListening();
          } else {
            const intent = this.detectResearchIntent(finalText);
            if (intent.isResearch) {
              this.lastResearchTopic = finalText;
              await this.runReporter(finalText, { forceOpen: true, speak: true });
              this.voiceStage = 'reporting';
            } else {
              await this.runReporterFromFinalTranscript();
              this.voiceStage = 'reporting';
            }
          }
        } catch (err) {
          console.warn('onresult flow error:', err?.message || err);
          this.voiceStage = 'idle';
        }
      }, 500);
    }
  };

  rec.onend = () => {
    if (this.voiceStage === 'listening') this.voiceStage = 'idle';
    this.stopWaveInput();
  };

  rec.onerror = (e) => {
    console.warn('SpeechRecognition error:', e?.error || e);
    this.recognizing = false;
    this.voiceStage = 'idle';
    this.stopWaveInput();
  };

  this.recognition = rec;
},
async speakThenListen(html) {
  this.readOutLoud(html); // onend will auto-listen if toggle is on
},


    async toggleVoice() {
      if (!this.voiceSupported) return;
      if (this.recognizing) { this.stopVoice(); return; }
      await this.startVoice();
    },

  async startVoice() {
    this.showAnswering = false;
    if (!this.recognition) return;
    this.finalTranscript = '';
    this.interimTranscript = '';
    this._finalBuffer = '';
    clearTimeout(this._finalDebounceId);

    try {
      await this.startWaveInput();
      this.recognition.start();
      this.recognizing = true;
      this.voiceStage = 'listening';
    } catch (e) {
      this.recognizing = false;
      this.voiceStage = 'idle';
      this.stopWaveInput();
    }
  },

stopVoice() {
  try {
    this.recognition && this.recognition.stop();
  } catch (e) {
    console.warn("SpeechRecognition stop() failed:", e);
  }
  this.recognizing = false;
  this.stopWaveInput();
  if (this.voiceStage === 'listening') this.voiceStage = 'idle';
},

truncate(text = "", max = 360) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
},

appendQALog(question, answer) {
  const q = this.truncate(question || "");
  const a = this.truncate(
    (answer || "").toString().replace(/\n+/g, " ")
  );

  this.log(
    `<div class="qa-log">
       <div><strong>Q:</strong> ${q || "(empty)"}</div>
       <div style="margin-top:4px;"><strong>A:</strong> ${a || "(no answer)"}</div>
     </div>`
  );
},

resetGeneralForm() {
  this.question = "";
  this.finalTranscript = "";
  this.interimTranscript = "";
  this.$nextTick(() => {
    if (this.$refs.questionBox) this.$refs.questionBox.blur();
    setTimeout(() => this.$refs.questionBox && this.$refs.questionBox.focus(), 10);
  });
},

    handleVoiceCommand(text) {
      const t = text.trim();
      const lower = t.toLowerCase();

      const triggersUrl = /\b(https?:\/\/|www\.|[a-z0-9-]+\.(com|net|org|io|ai|gov|edu)\b|dot\s+com)\b/i.test(t);
      const saysResearch = /\bresearch|browse|open\b/i.test(t);
      const mLeads = lower.match(/^find\s+leads?\s+(?:in|for)?\s*(.+)$/i);
      if (mLeads) {
        this.mode = "leads";
        const parsed = this.parseLeadsFromUtterance(mLeads[1]?.trim() || "");
        if (parsed.location) this.leadForm.location = parsed.location;
        if (parsed.industries.length) this.leadForm.industries = parsed.industries;
        if (parsed.services.length) this.leadForm.services = parsed.services;
        this.runLeads();
        this.stopVoice();
        return;
      }

      const mComp = lower.match(/^(?:competitor(?:s)?\s+research|research\s+competitors?|compare)\s*(.+)?$/i);
      if (mComp) {
        this.mode = "competitors";
        const rest = (mComp[1] || "").trim();
        if (rest) this.competitorQuery = rest;
        this.runCompetitors();
        this.stopVoice();
        return;
      }
      const mGeneral = lower.match(/^(what is|who is|where is|when is|how is|when is|hello|how are you|hi)\s+(.+)/i);
      if (mGeneral) {
        this.mode = "general";
        this.question = (mGeneral[2] || "").trim();
        if (triggersUrl || saysResearch) this.autoResearch = true;
        this.runGeneral();
        this.stopVoice();
        return;
      }
      if (t.length > 3) {
        this.mode = "general";
        this.question = t;
        if (triggersUrl || saysResearch) this.autoResearch = true;
        this.runGeneral();
        this.stopVoice();
      }
    },
    async startWaveInput() {
      if (this.audioCtx) return;
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = this.audioCtx.createMediaStreamSource(this.stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 512;
      source.connect(this.analyser);
    },
stopWaveInput() { this.cleanupAudio(true); },

    cleanupAudio(full = true) {

      if (full) {
try { if (this.analyser) this.analyser.disconnect(); } 
catch (e) { /* safely ignore analyser disconnect errors */ }

try { if (this.audioCtx) this.audioCtx.close(); } 
catch (e) { /* safely ignore audio context close errors */ }

        if (this.stream) this.stream.getTracks().forEach(t => t.stop());
        this.analyser = null; this.audioCtx = null; this.stream = null;
      }
    },
    drawWave() {
      const wave = this.$refs.wave;
      const bars = this.$refs.bars;
      if (!wave || !bars) return;

      const wctx = wave.getContext("2d");
      const bctx = bars.getContext("2d");
      const dpr = window.devicePixelRatio || 1;

      const resize = () => {
        const w = 680, h = 126;
        wave.width = w * dpr; wave.height = h * dpr;
        wave.style.width = w + "px"; wave.style.height = h + "px";
        wctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const bw = 560, bh = 36;
        bars.width = bw * dpr; bars.height = bh * dpr;
        bars.style.width = bw + "px"; bars.style.height = bh + "px";
        bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize(); window.addEventListener("resize", resize);

      const N_BARS = 16;
      const render = () => {
        let instant = 0;
        if (this.analyser) {
          const buf = new Uint8Array(this.analyser.frequencyBinCount);
          this.analyser.getByteFrequencyData(buf);
          let sum = 0, count = 0;
          const start = 2, end = Math.min(48, buf.length);
          for (let i = start; i < end; i++) { sum += buf[i]; count++; }
          instant = (sum / Math.max(1, count)) / 255; // 0..1
        } else if (this.loading) {
          instant = 0.25 + 0.3 * Math.sin(performance.now() / 450);
        } else {
          instant = 0; // idle
        }
        this.waveLevel = this.waveLevel * 0.85 + instant * 0.15;
        const ww = wave.width / dpr, wh = wave.height / dpr;
        wctx.clearRect(0, 0, ww, wh);

        const t = performance.now() / 700;
        const baseAmp = this.recognizing ? 36  : (this.loading ? 14 : 8);
        const speakBoost = 120 * this.waveLevel;
        const amp = baseAmp + speakBoost;

        const thickness = this.recognizing ? (1.5 + this.waveLevel * 3.2) : (this.loading ? 2.4 : 2);
        const glow = 10 + this.waveLevel * 26;
        const hue = this.loading ? 200 : (180 + Math.round(this.waveLevel * 90));
        const light = 68 + Math.round(this.waveLevel * 20);
        const color = `hsla(${hue}, 100%, ${light}%, 0.95)`;

        wctx.beginPath();
        wctx.moveTo(0, wh/2);
        for (let x = 0; x <= ww; x += 2) {
          const y =
            wh/2
            + Math.sin((x/38) + t*4.0) * amp
            + Math.sin((x/16) + t*7.2) * (amp * 0.18);
          wctx.lineTo(x, y);
        }
        wctx.strokeStyle = color;
        wctx.lineWidth = thickness;
        wctx.shadowColor = `hsla(${hue}, 100%, ${light}%, 0.75)`;
        wctx.shadowBlur = glow;
        wctx.stroke();
        wctx.shadowBlur = 0;
        const bw = bars.width / dpr, bh = bars.height / dpr;
        bctx.clearRect(0, 0, bw, bh);

        const gap = 12;
        const barW = (bw - gap*(N_BARS-1)) / N_BARS;

        let levels = new Array(N_BARS).fill(0);
        if (this.analyser) {
          const buf = new Uint8Array(this.analyser.frequencyBinCount);
          this.analyser.getByteFrequencyData(buf);
          for (let i = 0; i < N_BARS; i++) {
            const lo = 2 + Math.floor(i * 3);
            const hi = Math.min(lo + 3, buf.length);
            let s = 0, c = 0;
            for (let k = lo; k < hi; k++) { s += buf[k]; c++; }
            levels[i] = (s / Math.max(1,c)) / 255; // 0..1
          }
        } else {
          for (let i = 0; i < N_BARS; i++) {
            levels[i] = (this.loading ? 0.25 : 0.1) + 0.08 * Math.sin(t*5 + i*0.7);
          }
        }
        const barHue = hue, barColor = (a)=>`hsla(${barHue}, 100%, 60%, ${a})`;
        for (let i = 0; i < N_BARS; i++) {
          const x = i * (barW + gap);
          const h = Math.max(2, (bh - 4) * Math.max(0, Math.min(1, levels[i] * (0.6 + this.waveLevel))));
          const y = bh - h;
          bctx.fillStyle = barColor(0.85);
          bctx.fillRect(x, y, barW, h);
          bctx.fillStyle = barColor(0.35);
          bctx.fillRect(x, y - 2, barW, 2);
        }

        const ring = this.$refs.voiceRing;
        if (ring) {
          const scale = 0.95 + this.waveLevel * 0.8;
          const op = 0.35 + this.waveLevel * 0.55;
          ring.style.transform = `scale(${scale})`;
          ring.style.opacity = `${Math.min(0.95, op)}`;
          ring.style.borderColor = `hsla(${hue}, 90%, 70%, ${0.55 + this.waveLevel * 0.35})`;
        }

        this.rafId = requestAnimationFrame(render);
      };
      this.drawBarsClear = () => {
        const bw = bars.width / dpr, bh = bars.height / dpr;
        bctx.clearRect(0, 0, bw, bh);
      };

      render();
    },


    globalHotkeys(e) {
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : "";
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;

      if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        this.toggleVoice();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        this.focusInput();
      }
      if (e.key === "Enter" && this.mode === "general" && !this.recognizing) {
        this.runGeneral();
      }
    },

    focusInput() {
      this.$nextTick(() => this.$refs.questionBox && this.$refs.questionBox.focus());
    },
    capFirst(s="") { return s.replace(/\b\w/g, c => c.toUpperCase()); },

    /* =================== Prompts =================== */
buildSystemPrompt() {
  return `
You are a broadly knowledgeable AI assistant who can answer questions on ANY topic.

Primary domain expertise (use when relevant): Music artist tour research, planning, and market analysis.
- Core tasks: discover past & upcoming tour dates, confirm announcements, compile venues & capacities, detect on-sales/presales, map routing, analyze demand, and surface key contacts (management/agency/promoter) from reputable sources.
- Preferred sources: Official artist/management sites and socials; Ticketmaster/Live Nation, AXS, Bandsintown, Setlist.fm, Songkick, Pollstar (if accessible), Spotify artist pages, Apple Music, YouTube, Wikipedia (for context), venue websites, and major press outlets.

Behavior rules:
1) If the user's question is RELATED to artists/tours (dates, venues, routing, tickets, festivals, markets, production needs):
   - Prioritize latest official announcements and reputable listings.
   - Distinguish CONFIRMED vs RUMORED. Mark status explicitly.
   - Provide concrete, actionable outputs (lists/tables) with: date, city, venue, capacity (if known), on-sale info, ticket/link, source URL, confidence.
   - When relevant, include routing insights (drive/flight legs), regional clustering, venue capacity bands, and demand signals (sold out, low tickets, trending).
   - Offer next-step options (e.g., “Check Ticketmaster for presale code,” “Add to calendar,” “Compare venues by capacity,” “Generate route map,” “Draft outreach email to venue/agent.”)

2) If the user's question is NOT related to tours:
   - Answer normally with the best general knowledge.
   - Do NOT force music/touring content.

3) Freshness & research:
   - If details may be outdated/uncertain OR the user asks to research/browse OR includes URLs/domains, perform web research.
   - Use the format: ACTION:SEARCH: <best query> when proposing an external search, and prefer multiple queries if helpful (e.g., “Artist name site:ticketmaster.com”, “Artist name Setlist.fm 2024-2026”, “Artist name ‘tour’ ‘announced’ month year”).

4) Output conventions:
   - For structured results, provide a concise table or JSON block with fields:
     { "date": "YYYY-MM-DD", "city": "City, Region/Country", "venue": "", "capacity": <int|null>, "status": "confirmed|rumor|canceled|postponed", "onsale": "date/info|null", "ticket_url": "", "source": "", "confidence": 0-1 }
   - When giving analysis, be brief and highlight patterns (market clusters, venue tiers, typical routing).
   - When unsure, state uncertainty and show how to verify.

5) Compliance & IP:
   - Do not reproduce full lyrics or paywalled content. Paraphrase when needed.
   - Attribute sources clearly; avoid low-trust rumors unless labeled as such.

General style:
- Be accurate, clear, and helpful; use short bullet points and checklists when useful.
- Include concrete next steps and links users can act on.
`.trim();
},


async chatComplete(systemOrMessages, userPrompt = null, model = "gpt-4o") {
  if (!this.gptApiKey) return "(No OpenAI key configured in client)";
  const messages = Array.isArray(systemOrMessages)
    ? systemOrMessages.map(m => ({ role: m.role, content: String(m.content || "") }))
    : [{ role: "system", content: String(systemOrMessages || "") }, { role: "user", content: String(userPrompt || "") }];

  const res = await axios.post(
    "https://api.openai.com/v1/chat/completions",
    { model, messages, temperature: 0.2, max_tokens: 5000 },
    { headers: { Authorization: `Bearer ${this.gptApiKey}` } }
  );
  return String(res.data?.choices?.[0]?.message?.content || "").trim();
},

async cseSearch(query, tag = "General") {
  if (!this.googleApiKey || !this.cseCx) throw new Error("Missing Google CSE key or cx.");
  const res = await axios.get("https://www.googleapis.com/customsearch/v1", {
    params: { key: this.googleApiKey, cx: this.cseCx, q: query, num: 5 }
  });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  const mapped = items.map(i => ({ title: i.title, link: i.link, snippet: i.snippet, tag }));
  this.googleResults = this.uniqueByLink(mapped);
  this.searchPreview = items.slice(0, 6).map(i => ({ title: i.title, snippet: i.snippet, link: i.link, tag }));
  return this.googleResults;
},

    cycleTab(delta = 1) {
      if (!this.tabs.length) return;
      const idx = Math.max(0, this.tabs.findIndex(t => t.id === this.activeTabId));
      this.activeTabId = this.tabs[(idx + delta + this.tabs.length) % this.tabs.length].id;
    },
    setTabStatus(id, status) {
      const t = this.tabs.find(x => x.id === id);
      if (t) t.status = status;
    },
    simulateProgress(id) {
      const t = this.tabs.find(x => x.id === id);
      if (!t) return;
      let p = 0;
      const tick = () => {
        p = Math.min(100, p + Math.random()*15);
        t.progress = p;
        if (p < 100 && t.status !== "done") requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },

    /* =================== Timeline / Utils =================== */
    log(msg, meta = null) {
      const now = new Date();
      this.timeline.unshift({ time: now.toLocaleTimeString(), msg, meta });
    },
    clearConsole() {
      this.tabs = [];
      this.activeTabId = null;
      this.timeline = [];
      this.googleResults = [];
    },
   
onceFlag(name, val = true) {
  this.__flags = this.__flags || Object.create(null);
  if (this.__flags[name]) return false; // already set
  this.__flags[name] = val;
  return true;
},
clearFlag(name) { if (this.__flags) delete this.__flags[name]; },

uniqueByLink(list = []) {
  const seen = new Set();
  return (list || []).filter(x => {
    const k = (x?.link || '').trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
},
ensureHttp(u = "") {
  const s = String(u || "").trim();
  return /^https?:\/\//i.test(s) ? s : (s ? `https://${s}` : "");
},
// one listener, no duplicates — uses event delegation
delegateLinks(container, handler) {
  if (!container || !this.onceFlag('__repLinksDelegated')) return;
  container.addEventListener('click', (ev) => {
    const a = ev.target?.closest?.('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!href || href.startsWith('#')) return;
    if (ev.metaKey || ev.ctrlKey) return; // let new-tab behavior work
    ev.preventDefault();
    handler(href, a.textContent?.trim() || '');
  }, { passive: false });
},
// smooth scroll with safe fallback
scrollIntoViewSafe(el) {
  if (!el) return;
  try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  catch (e) { try { el.scrollIntoView(true); } catch (_) {/* noop */} }
},




async runGeneral() {
  const rawQ = (this.question || "").trim();
  if (!rawQ) return alert("Enter a question.");
  this.resetOutput();

  // 1) Handle pure “move tab to screen X” voice commands before doing any AI work
  try {
    if (this.handleScreenVoiceCommand) {
      const screenResult = await this.handleScreenVoiceCommand(rawQ);
      if (screenResult?.handled) {
        // We already moved the tab; log and exit gracefully
        this.appendQALog(rawQ, "(Moved tab between screens)");
        this.resetGeneralForm();
        this.statusMsg = "";
        this.loading = false;
        this.stage = "idle";
        this.resetMeters();
        return;
      }

      // Optional: keep the detected targetScreen to open new tabs on
      this.targetScreenForRun =
        screenResult?.targetScreen || this.activeScreen || 1;
    } else {
      this.targetScreenForRun = this.activeScreen || 1;
    }
  } catch (e) {
    console.warn("Screen voice handling failed:", e);
    this.targetScreenForRun = this.activeScreen || 1;
  }

  // Optionally strip phrases like "on screen 2" from the actual AI question
  const q = this.stripScreenPhrases
    ? this.stripScreenPhrases(rawQ)
    : rawQ;

  try {
    const intent = this.detectResearchIntent
      ? this.detectResearchIntent(q)
      : { isResearch: false, urls: [] };

    if (intent.isResearch) {
      this.statusMsg = "Launching AI Reporter…";
      await this.runReporter(q, { forceOpen: true, speak: true });
      this.appendQALog(q, "(Opened AI Reporter)");
      this.resetGeneralForm();
      this.statusMsg = "";
      this.loading = false;
      this.stage = "idle";
      this.resetMeters();
      return; // Reporter owns the flow; do not continue into Q&A
    }
  } catch (e) {
    console.warn("Intent check failed:", e);
  }

  try {
    this.loading = true;
    const sys = this.buildSystemPrompt();

    this.stage = "compose";
    this.statusMsg = "Thinking…";
    let answer = await this.chatComplete(sys, q);
    this.response.gptMessage = answer;
    this.response.message = answer;
    this.triggerAnsweringBg();

    const speakText = (this.ttsSanitizeNarration
      ? this.ttsSanitizeNarration(answer)
      : answer);
    this.readOutLoud(speakText);

    const urls = this.extractUrls ? this.extractUrls(q) : [];
    const searchRequests =
      (this.extractSearchIntent ? this.extractSearchIntent(answer) : null) ||
      (this.autoResearch
        ? (this.buildAutoQuery ? this.buildAutoQuery(q) : null)
        : null);

    if (this.autoResearch && (urls.length || searchRequests)) {
      this.stage = "search";
      this.statusMsg = "Researching online…";

      const tabsArr = this.tabs || [];
      const hasScreen1Tabs = tabsArr.some(
        t => (t.screenId || 1) === 1
      );
      const hasScreen2Tabs = tabsArr.some(
        t => t.screenId === 2
      );

      let targetScreen = this.targetScreenForRun || null;

      if (!targetScreen) {
        if (hasScreen1Tabs && !hasScreen2Tabs) {
          targetScreen = 2;
        } else if (!hasScreen1Tabs && hasScreen2Tabs) {
          targetScreen = 1;
        } else if (!hasScreen1Tabs && !hasScreen2Tabs) {
          targetScreen = this.activeScreen || 1;
        } else {
          targetScreen = this.activeScreen || 1;
        }
      }

      const googleTab = this.openTab(
        `https://www.google.com/search?q=${encodeURIComponent(
          searchRequests || q
        )}`,
        "google",
        "Google Search",
        targetScreen
      );

      const cseItems = await this.cseSearch(searchRequests || q, "research");
      this.setTabStatus(googleTab, "done");

      const sources = [];
      for (const u of urls.slice(0, 5)) {
        if (this.tabs?.some(t => t.url === u)) continue; // already opened

        const tabId = this.openTab(
          u,
          "page",
          this.prettyUrl ? this.prettyUrl(u) : "",
          targetScreen
        );

        try {
          const crawled = await this.crawlUrl(u);
          sources.push({
            title: crawled.title || (this.prettyUrl ? this.prettyUrl(u) : u),
            link: u,
            snippet: crawled.snippet || "",
            tag: "site"
          });
          this.knowledgeBase.push({
            title: crawled.title || (this.prettyUrl ? this.prettyUrl(u) : u),
            link: u
          });
          this.setTabStatus(tabId, "done");
        } catch (e) {
          const msg =
            e?.response?.data?.error?.message ||
            e?.message ||
            "Unknown error";
          this.error.message = msg;
          this.setTabStatus(tabId, "error");
        }
        await this.sleep(100);
      }

      const topCse = cseItems.slice(0, 8);
      this.response.onlineResults = this.uniqueByLink([
        ...sources,
        ...topCse
      ]);

      this.stage = "extract";
      this.statusMsg = "Extracting key facts…";
      this.pushToKnowledgeBase(this.response.onlineResults);

      this.stage = "compose";
      this.statusMsg = "Composing answer…";
      const synthQ =
        `Using these sources, refine the answer; summarize and list links:\n` +
        this.response.onlineResults
          .slice(0, 12)
          .map(r => `- ${r.title}: ${r.snippet}`)
          .join("\n") +
        `\nUser question: ${q}`;
      answer = await this.chatComplete(sys, synthQ);
      this.response.gptMessage = answer;
      this.response.message = answer;
      this.triggerAnsweringBg();
      this.readOutLoud(
        this.ttsSanitizeNarration
          ? this.ttsSanitizeNarration(answer)
          : answer
      );
    }

    this.appendQALog(q, this.response.gptMessage);
    this.resetGeneralForm();
    this.statusMsg = "";
  } catch (e) {
    const msg =
      e?.response?.data?.error?.message || e?.message || "Unknown error";
    this.error.message = msg;
    this.statusMsg = "";
    alert("Error: " + msg);
  } finally {
    this.loading = false;
    this.stage = "idle";
    this.resetMeters();
    this.targetScreenForRun = null; // 👈 reset the renamed key
  }
},



prettyUrl(url) {
    try {
      const u = new URL(url);
      return u.hostname.replace(/^www\./, "") + u.pathname;
    } catch {
      return url;
    }
  },

  openTab(url, kind = "page", title = "", screenId) {
    if (!this.tabs) this.tabs = [];

    const id =
      Date.now().toString(36) + Math.random().toString(16).slice(2);

    const tab = {
      id,
      url,
      kind,
      title: title || this.prettyUrl(url),
      status: "loading",
      screenId: screenId || this.activeScreen || 1,
    };

    this.tabs.push(tab);
    this.activeTabId = id;
    return id;
  },

  closeTab(tabId) {
    const idx = this.tabs.findIndex(t => t.id === tabId);
    if (idx === -1) return;
    const wasActive = this.tabs[idx].id === this.activeTabId;
    this.tabs.splice(idx, 1);

    if (wasActive) {
      const last = this.tabs[this.tabs.length - 1];
      this.activeTabId = last ? last.id : null;
    }
  },

  moveTabToScreen(tabId, screenId) {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;
    tab.screenId = screenId;
  },



    async runLeads() {
      this.resetOutput();
      try {
        this.loading = true;

        this.stage = "search"; this.statusMsg = "Finding leads…";
        const queries = this.buildLeadQueries();
        this.log("Starting Google searches for leads…");
        const googleTab = this.openTab(`https://www.google.com/search?q=${encodeURIComponent(queries[0].query)}`, "google", "Leads Search");
        const seen = new Set();
        const all = [];

        for (const q of queries) {
          const results = await this.cseSearch(q.query, q.tag);
          for (const it of results) {
            const link = this.normalizeUrl(it.link);
            if (!seen.has(link)) { seen.add(link); all.push({ ...it, link }); }
            if (all.length >= this.leadForm.limit) break;
          }
          if (all.length >= this.leadForm.limit) break;
          await this.sleep(200);
        }
        this.setTabStatus(googleTab, "done");

        this.stage = "extract"; this.statusMsg = "Extracting contacts & opportunities…";
        this.pushToKnowledgeBase(all);

        this.stage = "compose"; this.statusMsg = "Preparing outreach suggestions…";
        const sys = this.buildSystemPrompt();
        const pitchQ =
          `Given these lead snippets, propose the best contact and a 3-line pitch per segment for Instaguard in Los Angeles:\n` +
          all.slice(0, 12).map(r => `- ${r.title}: ${r.snippet}`).join("\n");
        const gpt = await this.chatComplete(sys, pitchQ);

        this.response.gptMessage = gpt;
        this.triggerAnsweringBg();
        this.readOutLoud(this.response.gptMessage);
        this.response.onlineResults = this.uniqueByLink(all.slice(0, this.leadForm.limit));
        this.statusMsg = "";
      } catch (e) {
        this.statusMsg = "";
        alert(e.message);
      } finally {
        this.loading = false; this.stage = "idle";
        this.resetMeters(); 
      }
    },

    async runCompetitors() {
      this.resetOutput();
      try {
        this.loading = true;

        this.stage = "search"; this.statusMsg = "Researching competitors…";
        this.log("Starting competitor search…");
        const gtab = this.openTab(`https://www.google.com/search?q=${encodeURIComponent(this.competitorQuery)}`, "google", "Competitor Search");
        const results = await this.cseSearch(this.competitorQuery, "Competitors");
        this.setTabStatus(gtab, "done");

        this.stage = "extract"; this.statusMsg = "Extracting differentiators…";
        this.pushToKnowledgeBase(results);

        this.stage = "compose"; this.statusMsg = "Drafting positioning…";
        const sys = this.buildSystemPrompt();
        const summaryQ =
          `Analyze these competitor snippets and list 5 differentiators Instaguard can highlight:\n` +
          results.slice(0, 10).map(r => `- ${r.title}: ${r.snippet}`).join("\n");
        const gpt = await this.chatComplete(sys, summaryQ);

        this.response.gptMessage = gpt;
        this.triggerAnsweringBg();
        this.readOutLoud(this.response.gptMessage);
        this.response.onlineResults = this.uniqueByLink(results);
        this.statusMsg = "";
      } catch (e) {
        this.statusMsg = "";
        alert(e.message);
      } finally {
        this.loading = false; this.stage = "idle";
        this.resetMeters(); 
      }
    },
    extractSearchIntent(answer) {
      const m = answer.match(/ACTION:SEARCH:\s*(.+)$/im);
      return m ? m[1].trim() : null;
    },
    buildAutoQuery(q) {
      return q.replace(/\b(browse|open|show|research)\b/gi, "").trim();
    },
    pushToKnowledgeBase(items) {
      const current = new Set(this.knowledgeBase.map(k => this.normalizeUrl(k.link)));
      for (const i of items.slice(0, 50)) {
        const link = this.normalizeUrl(i.link);
        if (!link || current.has(link)) continue;
        current.add(link);
        this.knowledgeBase.push({ title: i.title, link });
      }
    },
    buildLeadQueries() {
      const { services, industries, location, radius } = this.leadForm;
      const loc = location || "Los Angeles, CA";
      const byIndustry = industries.map(ind => ({
        tag: ind,
        query: `${ind} security ${loc} ${radius}mi ("RFP" OR "RFQ" OR "solicitation" OR "bid" OR "contract") -job -careers -indeed -ziprecruiter`
      }));
      const byService = services.map(svc => ({
        tag: svc,
        query: `"${svc}" services ${loc} ${radius}mi ("request for proposal" OR "RFP" OR "bid" OR "contract") -indeed -ziprecruiter -jobs`
      }));
      const misc = [
        { tag: "Property Mgmt", query: `property management companies ${loc} ${radius}mi ("security" OR "patrol") contact` },
        { tag: "Construction", query: `construction sites ${loc} ${radius}mi ("site security" OR "guard") contact` },
        { tag: "Retail", query: `retail centers ${loc} ${radius}mi ("security" OR "loss prevention") management` },
        { tag: "Events", query: `event venues ${loc} ${radius}mi "security" contact` },
        { tag: "Film", query: `film production security ${loc} ${radius}mi contact` },
        { tag: "HOA", query: `HOA community security ${loc} ${radius}mi management contact` },
      ];
      return [...byIndustry, ...byService, ...misc];
    },
    addSource(item) {
      const norm = this.normalizeUrl(item.link);
      if (this.isInSources(norm)) {
        this.log(`Skipped duplicate source: <b>${this.prettyUrl(norm)}</b>`);
        return;
      }
      this.response.onlineResults = this.uniqueByLink([
        ...this.response.onlineResults,
        { title: item.title || this.prettyUrl(norm), snippet: item.snippet || "", link: norm, tag: item.tag || "research" }
      ]);
      this.log(`Added source: <b>${item.title || this.prettyUrl(norm)}</b>`);
    },
    resetOutput() {
      this.response = { gptMessage: "", onlineResults: [] };
      this.statusMsg = "";
      this.searchPreview = [];
      this.stage = "idle";
    },

    exportResultsCSV() {
      const rows = [["#", "Title", "Snippet", "Tag", "Link"]];
      this.mergedResults.forEach((r, i) => rows.push([i + 1, r.title, r.snippet, r.tag, r.link]));
      const csv = rows.map(r => r.map(x => `"${String(x ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = "instaguard_research.csv"; link.click();
    },
    exportResultsPDF() {
      const doc = new jsPDF();
      doc.text("Instaguard Unified Research", 10, 10);
      let y = 20;
      this.mergedResults.slice(0, 28).forEach((r, i) => {
        doc.text(`${i + 1}. ${r.title || this.prettyUrl(r.link)} [${r.tag || "research"}]`, 10, y); y += 6;
        doc.text(`- ${(r.snippet || "").slice(0, 150)}`, 10, y); y += 8;
        if (y > 270) { doc.addPage(); y = 20; }
      });
      doc.save("instaguard_research.pdf");
    },

    sleep(ms) { return new Promise(r => setTimeout(r, ms)); },

    parseLeadsFromUtterance(s = "") {
      const servicesList = ["unarmed guard","armed guard","mobile patrol","event security","alarm response","standing post","standing guard"];
      const industriesList = ["apartments","property management","construction","retail","hoa","event venues","nightclubs","film production","hospitals","schools","logistics","warehouse","car dealerships","parking lots"];

      const services = servicesList.filter(w => new RegExp(`\\b${w}\\b`, "i").test(s));
      const industries = industriesList.filter(w => new RegExp(`\\b${w}\\b`, "i").test(s));

      let location = "";
      const locMatch = s.match(/\b(?:in|for)\s+(.+)$/i);
      if (locMatch) location = locMatch[1].trim();
      if (!location) {
        const scrub = s
          .replace(new RegExp(servicesList.join("|"), "ig"), "")
          .replace(new RegExp(industriesList.join("|"), "ig"), "")
          .replace(/\b(find|leads?|security|near|around|within|\d+mi)\b/ig, "")
          .trim();
        if (scrub) location = scrub;
      }

      return { location, industries, services };
    },
  },
};
</script>
<style scoped>


/* optional: push main content down so it isn't hidden under the overlay */
.dashboard-content {
  padding-top: 96px;      /* tweak height as needed based on your header/tabs */
}

.screens-layout {
  position: fixed;          /* float above everything */
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;            /* 👈 above all content */
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  box-sizing: border-box;
  pointer-events: auto;
}

/* Push the rest of the dashboard down a bit so it's not hidden under overlay */
.bg-content .wrap {
  padding-top: 96px;        /* adjust if you want more/less space under screens */
}

.screen {
  flex: 1;
  min-width: 0;
  background: rgba(5, 10, 30, 0.96);
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.6);
  padding: 6px 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

/* Optional small helper when a screen has no tabs */
.rc-empty {
  font-size: 0.75rem;
  opacity: 0.6;
  padding: 4px 8px;
}


/* Smooth page scrolling (just in case) */
html { scroll-behavior: smooth; }

/* Flash ring for the target area after auto-scroll */
.focus-flash {
  animation: focusFlash 1.2s ease-out 1;
  box-shadow: 0 0 0 0 rgba(138, 43, 226, 0.35);
}
@keyframes focusFlash {
  0%   { box-shadow: 0 0 0 0 rgba(138, 43, 226, 0.0); }
  30%  { box-shadow: 0 0 0 6px rgba(138, 43, 226, 0.25); }
  60%  { box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.22); }
  100% { box-shadow: 0 0 0 0 rgba(138, 43, 226, 0.0); }
}

.voice-cta {
  transition: transform .15s ease;
}
.voice-cta:active {
  transform: scale(0.98);
}
.muted.tiny { font-size: 12px; opacity: .7; }
.mt-2 { margin-top: .5rem; }

/* ============================
   Futuristic opaque browser frame
   ============================ */

/* Hero area */
.rep-hero {
  position: relative;
  min-height: 320px;
  background: linear-gradient(180deg, rgba(10,14,20,0.85), rgba(10,14,20,0.35));
  overflow: hidden;
}

.rep-hero-media {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 18px;
}
.rep-followup {
  padding: 20px 20px 20px 20px;
  margin: 20px 20px 20px 20px;
  background: rgba(12,18,24,.55);
  backdrop-filter: blur(6px);
}
.rep-followup-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.rep-followup-actions {
  margin-top: 8px; display: flex; flex-wrap: wrap; gap: 8px;
}
.rep-vox { display:flex; align-items:center; gap:8px; }
.vox-state { background: rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); }
.vox-state.on { background: rgba(0,255,170,.10); border-color: rgba(0,255,170,.4); }

.rep-live-frame {
  --frame-bg: rgba(16, 22, 28, 0.55);
  --frame-br: 18px;
  position: relative;
  width: min(1225px, 92vw);
  aspect-ratio: 16 / 9;
  border-radius: var(--frame-br);
  background: var(--frame-bg);
  backdrop-filter: blur(6px) saturate(1.1);
  border: 1px solid rgba(120, 200, 255, 0.22);
  box-shadow:
    0 10px 40px rgba(0,0,0,0.35),
    inset 0 0 0 1px rgba(255,255,255,0.05),
    0 0 0 2px rgba(10, 200, 255, 0.06);
  overflow: hidden;
}
.rep-live-frame::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: calc(var(--frame-br) + 2px);
  background: radial-gradient(1200px 480px at 10% -10%, rgba(0,255,255,0.08), transparent 40%),
              radial-gradient(1200px 480px at 110% 110%, rgba(120,80,255,0.08), transparent 35%);
  pointer-events: none;
}
.rep-live-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Thumbnails */
.rep-hero-thumbs {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 12px;
  background: rgba(10,14,20,0.55);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255,255,255,0.08);
}
.rep-hero-thumb {
  width: 72px;
  height: 42px;
  object-fit: cover;
  border-radius: 8px;
  opacity: 0.85;
  outline: 1px solid rgba(255,255,255,0.15);
  cursor: pointer;
  transition: transform .15s ease, opacity .15s ease, outline-color .15s ease;
}
.rep-hero-thumb:hover { transform: translateY(-2px); opacity: 1; }
.rep-hero-thumb.active { outline-color: rgba(0,200,255,0.7); opacity: 1; }

/* Content overlay */
.rep-hero-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(7,10,14,0.65) 0%, rgba(7,10,14,0.35) 50%, rgba(7,10,14,0.85) 100%);
  pointer-events: none;
}
.rep-hero-content {
  position: relative;
  z-index: 2;
  padding: 26px clamp(16px, 5vw, 40px) 18px;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,.5);
}
.rep-title { margin: 8px 0 2px; font-size: clamp(24px, 3.2vw, 40px); line-height: 1.1; }
.rep-subtitle { opacity: .9; font-size: clamp(13px, 1.2vw, 16px); }

/* HUD chips */
.rep-hero-hud { display: flex; gap: 8px; margin-top: 10px; }
.hud-tag { background: rgba(15,25,35,.6); backdrop-filter: blur(4px); }

/* Responsive tweaks */
@media (max-width: 720px) {
  .rep-live-frame { width: 96vw; }
  .rep-hero-thumbs { bottom: 10px; gap: 6px; }
  .rep-hero-thumb { width: 60px; height: 36px; }
}


.rep-live-frame {
  /* Tunables */
  --border: 14px;            /* thick opaque border */
  --radius: 20px;            /* outer corner radius */
  --chrome-h: 46px;          /* top bar height */
  --url-radius: 12px;

  /* Accents */
  --acc1: #22d3ee;           /* cyan */
  --acc2: #a855f7;           /* purple */
  --acc3: #2bd4db;           /* teal */

  /* Surfaces */
  --frame-bg: linear-gradient(180deg,#0c121c 0%, #0a0f18 100%);
  --glass: rgba(255,255,255,0.05);
  --stroke: rgba(255,255,255,0.12);

  position: relative;
  display: grid;
  grid-template-rows: var(--chrome-h) 1fr;
  gap: 10px;

  padding: var(--border);
  border-radius: calc(var(--radius) + var(--border));
  background: var(--frame-bg);
  box-shadow:
    0 20px 60px rgba(0,0,0,.35),
    inset 0 1px 0 rgba(255,255,255,0.06);

  /* Neon edge glow (subtle) */
  overflow: hidden;
}

.rep-live-frame::before {
  /* Gradient glow ring *outside* content area */
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: var(--border);
  background:
    linear-gradient(135deg, rgba(34,211,238,.35), rgba(168,85,247,.35) 55%, rgba(43,212,219,.35));
  mask-composite: exclude;     /* Firefox */
  pointer-events: none;
  filter: blur(6px) saturate(1.1);
  opacity: .6;
}

/* Fallback for browsers without mask-composite */
@supports not ((-webkit-mask-composite: xor) or (mask-composite: exclude)) {
  .rep-live-frame::before {
    mask: none;
    -webkit-mask: none;
    padding: 0;
    background: radial-gradient(70% 120% at 50% 50%, rgba(34,211,238,.15), rgba(168,85,247,0) 70%);
    filter: blur(18px);
  }
}

/* ------- "Browser" chrome ------- */
.rep-live-frame__chrome {
  display: flex;
  align-items: center;
  gap: 12px;
  height: var(--chrome-h);
  padding: 8px 10px;
  border-radius: calc(var(--radius) - 2px);
  background:
    linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.02));
  border: 1px solid var(--stroke);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.14),
    inset 0 -1px 0 rgba(255,255,255,.06),
    0 8px 22px rgba(0,0,0,.25);
  backdrop-filter: blur(6px) saturate(1.2);
}

.rep-live-frame__traffic {
  display: inline-flex;
  gap: 8px;
  padding: 0 4px;
  min-width: 60px;
}
.rep-live-frame__traffic .dot {
  width: 11px; height: 11px; border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.35), 0 1px 2px rgba(0,0,0,.2);
}
.rep-live-frame__traffic .dot:nth-child(1) { background: #ff5f57; }
.rep-live-frame__traffic .dot:nth-child(2) { background: #febc2e; }
.rep-live-frame__traffic .dot:nth-child(3) { background: #28c840; }

.rep-live-frame__url {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px;
  border-radius: var(--url-radius);
  background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.03));
  border: 1px solid rgba(255,255,255,.14);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.25);
  color: rgba(255,255,255,.92);
  font-size: 12.5px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
}
.rep-live-frame__urltext {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ------- Viewport (your iframe) ------- */
.rep-live-frame__viewport,
.rep-live-frame__placeholder {
  width: 84.5%;
  max-width: 100%;
  height: auto;
  aspect-ratio: 16 / 10; /* nice default; prevents layout shift */
  border: 1px solid var(--stroke);
  border-radius: calc(var(--radius) - 2px);
  background: #0e1626;
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,.35),
    0 18px 40px rgba(0,0,0,.35);
}

.rep-live-frame__viewport {
  display: block;
}
.rep-live-frame__viewport {
  /* ensure iframe fills */
  width: 100%;
  height: 100%;
}

/* Placeholder when no tab is active */
.rep-live-frame__placeholder {
  display: grid;
  place-items: center;
  color: rgba(255,255,255,.65);
  font-size: 14px;
}

/* ------- States ------- */
.rep-live-frame.is-loading .rep-live-frame__viewport {
  position: relative;
}
.rep-live-frame.is-loading .rep-live-frame__viewport::after {
  content: "";
  position: absolute; inset: 0;
  border-radius: inherit;
  background:
    linear-gradient(90deg,
      rgba(255,255,255,0.02) 0%,
      rgba(255,255,255,0.05) 40%,
      rgba(255,255,255,0.02) 80%);
  animation: repShimmer 1.1s linear infinite;
}
@keyframes repShimmer {
  from { transform: translateX(-30%); }
  to   { transform: translateX(30%); }
}

.rep-live-frame.is-live::before {
  opacity: .9;
  filter: blur(8px) saturate(1.25);
}
.rep-live-frame.is-error::before {
  background: linear-gradient(135deg, rgba(239,68,68,.45), rgba(168,85,247,.25));
}

/* ------- Responsive tweaks ------- */
@media (max-width: 1260px) {
  .rep-live-frame { --border: 12px; --radius: 18px; }
  .rep-live-frame__url { height: 30px; font-size: 12px; }
}
@media (max-width: 960px) {
  .rep-live-frame { --border: 10px; --radius: 16px; }
  .rep-live-frame__chrome { gap: 10px; }
  .rep-live-frame__url { height: 28px; font-size: 11.5px; }
  .rep-live-frame__viewport,
  .rep-live-frame__placeholder { aspect-ratio: 16 / 12; }
}
@media (max-width: 640px) {
  .rep-live-frame { --border: 8px; --radius: 14px; }
  .rep-live-frame__traffic { min-width: 48px; gap: 6px; }
  .rep-live-frame__traffic .dot { width: 9px; height: 9px; }
  .rep-live-frame__url { height: 26px; font-size: 11px; }
  .rep-live-frame__viewport,
  .rep-live-frame__placeholder { aspect-ratio: 4 / 3; }
}

/* ------- Light theme polish ------- */
@media (prefers-color-scheme: light) {
  .rep-live-frame {
    --frame-bg: linear-gradient(180deg,#eef2f8 0%, #e9eef6 100%);
    --glass: rgba(255,255,255,0.7);
    --stroke: rgba(0,0,0,0.08);
    box-shadow:
      0 20px 50px rgba(0,0,0,.12),
      inset 0 1px 0 rgba(255,255,255,.85);
  }
  .rep-live-frame__chrome {
    background: linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.7));
    border-color: rgba(0,0,0,.06);
  }
  .rep-live-frame__url {
    background: rgba(255,255,255,.9);
    border-color: rgba(0,0,0,.08);
    color: #1f2937;
  }
  .rep-live-frame__viewport,
  .rep-live-frame__placeholder {
    background: #f5f7fb;
    border-color: rgba(0,0,0,.08);
  }
}

.rep-live-preview .preview-scroll {
  max-height: 160px;
  overflow: auto;
  background: rgba(255,255,255,0.03);
  border-radius: 8px;
  padding: 8px 10px;
}

.rep-spotlight .spot-content{ padding: 10px 14px; }
.rep-spotlight .spot-title{ font-weight:700; color:#eaf0ff; }
.rep-spotlight .spot-host{ color:#a8b4d6; font-size:.9rem; margin-top:2px; }
.rep-spotlight .spot-snippet{
  margin-top:10px; color:#dfe6ff; line-height:1.35;
  border-left:2px solid rgba(139,92,246,.35); padding-left:10px;
}
.rep-spotlight .spot-actions{ display:flex; gap:8px; align-items:center; margin-top:10px; }

.stage-chips.compact {
  display:flex;
  gap:8px;
  margin: 8px 0 4px;
}
.stage-chips .v-chip {
  opacity:.55;
  background: rgba(255,255,255,.06);
}
.stage-chips .v-chip.on {
  opacity:1;
  font-weight:700;
}

/* Voice caption */
.voice-caption .cap-title { display:flex; align-items:center; gap:6px; opacity:.9; }
.voice-caption .cap-line { margin-top:6px; }
.voice-caption .cap-line.interim { opacity:.65; font-style: italic; }
.voice-caption .final { font-weight: 700; }
.reporter-wrap{
  --glass: rgba(20,20,28,.52);
  --glow: 0 8px 26px rgba(88,0,255,.18);
  --line: rgba(255,255,255,.08);
  --fg: #e8ecff;
  --fg-dim: #b7c0e0;
  --fg-soft: #a4acc7;
  --accent: #8b5cf6;  /* violet */
  --accent-2:#22d3ee; /* cyan */
  color: var(--fg);
  background:
    radial-gradient(1200px 400px at 30% -10%, rgba(139,92,246,.16), transparent 60%),
    radial-gradient(1200px 400px at 70% -8%, rgba(34,211,238,.18), transparent 62%),
    linear-gradient(180deg, #0c0f16 0%, #0b0e14 100%);
  min-height: 100vh;
}

/* ---------- HEADER ---------- */
.rep-header{
  position: sticky; top:0; z-index: 8;
  backdrop-filter: saturate(140%) blur(12px);
  background: linear-gradient(180deg, rgba(14,18,28,.72) 0%, rgba(43, 59, 99, 0.45) 60%, rgba(46, 66, 115, 0.45)00%);
  border-bottom: 1px solid var(--line);
  padding: 25px 35px 25px 35px;
}
.rep-header .rep-left, .rep-header .rep-right{
  display:flex; align-items:center; justify-content:space-between; gap:12px;
}
.rep-header .rep-right{ margin-top:8px; flex-wrap: wrap; }
@media(min-width:960px){
  .rep-header{ display:flex; align-items:center; justify-content:space-between; }
  .rep-header .rep-right{ margin-top:0; }
}
.rep-breadcrumb{ display:flex; align-items:center; gap:8px; color:var(--fg-dim); }
.rep-chip{ background: rgba(139,92,246,.18) !important; color:#d7c9ff !important; }
.rep-time{ opacity:.85; }

.onair{
  display:flex; align-items:center; gap:8px;
  background: rgba(255,74,74,.12) !important; color:#ff9a9a !important;
  border:1px solid rgba(255,74,74,.25) !important;
}
.onair .dot{
  width:8px; height:8px; border-radius:50%;
  background:#ff6060; box-shadow:0 0 12px #ff6060;
  animation: pulse 1.4s ease-in-out infinite;
}
.onair.live{ background: rgba(255,0,92,.16) !important; color:#ffb3cc !important; }
@keyframes pulse{ 0%{ transform: scale(1); opacity:1;} 60%{ transform:scale(.7); opacity:.7;} 100%{ transform:scale(1); opacity:1;} }

.rep-header-stats{ display:flex; gap:6px; margin-top:6px; }
.hud-chip{
  background: rgba(255,255,255,.06) !important; color:var(--fg-soft) !important;
  border: 1px solid var(--line) !important;
}
.rep-actions .v-btn{ box-shadow: none !important; }
.rep-progress{ margin-top:10px; }

/* ---------- HERO ---------- */
.rep-hero{
  position: relative; min-height: 260px;
  border-bottom: 1px solid var(--line);
  background-size: cover; background-position: center;
}
.rep-hero-shade{
  position:absolute; inset:0;
  background:
    linear-gradient(180deg, rgba(10,12,18,.3) 0%, rgba(10,12,18,.6) 50%, rgba(10,12,18,.85) 100%),
    radial-gradient(800px 200px at 35% -10%, rgba(139,92,246,.25), transparent 60%),
    radial-gradient(800px 200px at 75% -12%, rgba(34,211,238,.28), transparent 62%);
}
.rep-hero-content{
  position: relative; z-index:1; padding: 38px 22px 28px;
  max-width: 1100px; margin: 0 auto;
}
.rep-kicker{ letter-spacing:.14em; text-transform: uppercase; color:#b7faff; font-weight:600; opacity:.9; }
.rep-title{
  margin:10px 0 4px; font-size: clamp(28px, 5.6vw, 44px); line-height:1.1;
  text-shadow: 0 2px 16px rgba(34,211,238,.18), 0 2px 26px rgba(139,92,246,.16);
}
.rep-subtitle{ color:var(--fg-soft); max-width: 80ch; }
.rep-byline{ margin-top:8px; color:var(--fg-dim); }
.rep-hero-hud{ margin-top:14px; display:flex; gap:8px; }
.hud-tag{
  background: rgba(255,255,255,.06) !important; color:#d8e6ff !important;
  border:1px solid var(--line) !important;
}

/* ---------- BODY ---------- */
.rep-body{ padding: 18px 12px 90px; }
.glass{
  background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.03));
  border:1px solid var(--line) !important; box-shadow: var(--glow);
}
.rep-lede-card{ padding: 26px 28px; }
.rep-lede :deep(p){ font-size: 1.05rem; color:#eaf0ff; }
.rep-content{ padding: 24px 28px; }
.rep-content-inner :deep(h2){  font-size:0rem; padding: 25px 25px 25px 25px; top: -200px; margin: 18px 0 22px 30px; color:#d9e4ff; }
.rep-content-inner :deep(p){ color:#dfe6ff; }
.rep-content-inner :deep(ul), .rep-content-inner :deep(ol){ padding-left: 1.25rem; }

.rep-answer{ overflow: hidden; }
.rep-answer-head{
  padding: 12px 14px; font-weight: 600; color:#e9efff;
  background: linear-gradient(90deg, rgba(139,92,246,.18), rgba(34,211,238,.14));
  border-bottom: 1px solid var(--line);
}
.rep-answer-body{
  padding: 12px 14px; font-weight:600; color:#e9efff;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.02));
}

/* ---------- RIGHT RAIL ---------- */
.rep-side-title{
  padding: 12px 14px; font-weight:600; color:#e9efff;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.02));
}
.rep-source-list{ max-height: 420px; overflow:auto; }
.rep-source-item{
  display:grid; grid-template-columns: 1fr auto; gap:6px 10px;
  padding: 10px 14px; border-bottom: 1px dashed var(--line);
  padding: 12px 14px; font-weight:600; color:#e9efff;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.02));
}
.rep-source-item .src-title{ font-weight: 600; color:#eaf0ff; }
.rep-source-item .src-host{ grid-column:1 / -1; font-size: .85rem; color:var(--fg-soft); opacity:.85; }
.rep-source-item .src-actions{ display:flex; gap:8px; align-items:center; }

.rep-facts-list, .rep-steps{ list-style: none; padding: 8px 14px 14px; margin:0; }
.rep-facts-list li{
  position: relative; padding:8px 10px 8px 22px; color:#e8efff;
  border:1px dashed var(--line); border-radius: 8px; margin: 7px 0;
  background: rgba(255,255,255,.03);
}
.rep-facts-list li::before{
  content:""; position:absolute; left:8px; top:14px; width:6px; height:6px; border-radius:50%;
  background: var(--accent-2); box-shadow: 0 0 10px var(--accent-2);
}
.rep-steps li{
  padding:9px 10px; margin:6px 0; border:1px solid var(--line); border-radius:8px;
  color:#e9f1ff; background: rgba(255,255,255,.02);
}

/* ---------- RUNNING ORDER ---------- */
.runlist{ max-height: 340px; overflow:auto; padding: 8px; }
.run-item{
  display:grid; grid-template-columns: 28px 22px 1fr; align-items:center;
  gap:10px; padding:8px 10px; border-radius:10px; cursor: pointer;
  border:1px solid rgba(255,255,255,.06);
  background: linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.02));
  transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease;
}
.run-item + .run-item{ margin-top:8px; }
.run-item:hover{ transform: translateY(-1px); border-color: rgba(139,92,246,.24); }
.run-item.active{
  border-color: rgba(139,92,246,.45);
  box-shadow: 0 0 0 2px rgba(139,92,246,.18) inset, var(--glow);
}
.run-item.done{ opacity:.7; }
.run-item .idx{
  display:inline-flex; justify-content:center; align-items:center;
  width:22px; height:22px; border-radius:6px;
  background: rgba(139,92,246,.16); color:#e9ddff; font-weight:700; font-size:.8rem;
}
.run-item .icon :deep(svg){ width:18px; height:18px; opacity:.9; }
.run-item .txt{ color:#eaf0ff; }

/* ---------- URL CARD / TICKER ---------- */
.rep-urlcard{
  position: fixed; z-index: 8; right:16px; top:84px;
  padding: 10px 12px; border-radius: 12px; border:1px solid var(--line);
  background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.04));
  box-shadow: var(--glow);
}
.rep-urlcard a{ color:#bfefff; text-decoration: none; word-break: break-all; }
.rep-urlcard .v-icon{ margin-right: 6px; }

.rep-ticker{
  position: fixed; left: 18px; right: 18px; bottom: 16px; z-index: 9;
  display:flex; align-items:center; gap:10px;
  padding: 12px 14px;
  background: linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.5));
  backdrop-filter: blur(8px) saturate(140%);
  border: 1px solid var(--line); border-radius: 12px;
  box-shadow: var(--glow);
}
.rep-ticker .dot{
  width:10px; height:10px; border-radius:50%; background:#3df3ff;
  box-shadow: 0 0 16px rgba(61,243,255,.9);
  animation: glow 1.6s ease-in-out infinite;
}
.rep-ticker .ticker-text{
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  width:100%; color:#e9f7ff;
}
@keyframes glow{ 0%{ opacity: .6 } 60%{ opacity:1 } 100%{ opacity:.6 } }

/* ---------- TRANSITIONS ---------- */
.fade-up-enter-active, .fade-up-leave-active{ transition: opacity .18s ease, transform .18s ease; }
.fade-up-enter, .fade-up-leave-to{ opacity:0; transform: translateY(6px); }

.slide-up-enter-active, .slide-up-leave-active{ transition: transform .22s ease, opacity .22s ease; }
.slide-up-enter, .slide-up-leave-to{ opacity:0; transform: translateY(12px); }

/* Scrollbars (WebKit) */
.rep-source-list::-webkit-scrollbar,
.runlist::-webkit-scrollbar{ height:8px; width:8px; }
.rep-source-list::-webkit-scrollbar-thumb,
.runlist::-webkit-scrollbar-thumb{
  background: rgba(255,255,255,.12); border-radius: 8px;
}

/* utilities */
.muted { opacity: .7; }
.open-link { margin-left: 8px; font-size: 12px; }
/* ===== Layout / Container ===== */

.chat-container {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 28px 22px 34px 28px;
  margin: 28px 22px 34px 28px;
  max-width: 1800px;
  box-shadow: 0 10px 40px rgba(0,0,0,.45);
  /* Smooth “lava lamp” gradient */
  --c1:#0f172a8f; --c2:#1e293b74; --c3:#111827; --c4:#0b1324;
  background: radial-gradient(1200px 600px at 10% 10%, #1e293b 0%, rgba(30,41,59,0) 55%),
              radial-gradient(900px 500px at 90% 30%, #0ea5e9 0%, rgba(14,165,233,0) 60%),
              radial-gradient(1000px 700px at 50% 90%, #7c3aed 0%, rgba(124,58,237,0) 60%),
              linear-gradient(120deg, var(--c1), var(--c2) 40%, var(--c3) 70%, var(--c4));
  background-size: 200% 200%;
  animation: lava 18s ease-in-out infinite;
  color: #e5e7eb;
}
@keyframes lava {
  0%,100% { background-position: 0% 50%; }
  50%     { background-position: 100% 50%; }
}

/* ===== Ambient blobs (soft) ===== */
.blobs { position:absolute; inset:0; pointer-events:none; filter: blur(48px) saturate(120%); opacity:.28; }
.blob { position:absolute; width:30vmax; height:30vmax; border-radius:50%; mix-blend-mode:screen; }
.b1 { left:-10vmax; top:-8vmax; background:#22d3ee33; animation: float 18s ease-in-out infinite; }
.b2 { right:-12vmax; top:-6vmax; background:#a78bfa2e; animation: float 24s ease-in-out infinite reverse; }
.b3 { left:20%; bottom:-10vmax; background:#60a5fa33; animation: float 22s ease-in-out infinite; }
.b4 { right:10%; bottom:-8vmax; background:#34d39933; animation: float 26s ease-in-out infinite reverse; }
@keyframes float { 0%,100%{ transform:translate3d(0,0,0); } 50%{ transform:translate3d(2vmax,-2vmax,0); } }

/* ===== Avatar / Mic / Viz ===== */
.avatar-container { display:flex; align-items:center; justify-content:center; flex-direction:column; gap:14px; }
.pulse-glow {
  width:110px; height:110px; border-radius:50%;
  box-shadow: 0 0 0 0 rgba(99,102,241,.65);
  background: radial-gradient(circle at 30% 30%, #3b82f6 0%, #0ea5e9 60%, transparent 68%);
  transition: filter .3s ease, transform .3s ease;
  animation: breathe 3.4s ease-in-out infinite;
}
.pulse-glow.thinking { filter:saturate(1.2) brightness(1.08); animation-duration:2.2s; }
@keyframes breathe { 0%,100%{ transform:scale(.98); box-shadow:0 0 0 0 rgba(99,102,241,.65);} 50%{ transform:scale(1.04); box-shadow:0 0 0 14px rgba(99,102,241,0);} }

.mic-wrap { position: relative; }
.mic-btn {
  width:46px; height:46px; border-radius:50%;
  border:1px solid #334155; background:#0b1220; color:#e2e8f0;
  transition: all .2s ease;
}
.mic-btn.on { background:#14532d; border-color:#16a34a; color:#ecfdf5; }
.mic-btn.disabled { opacity:.5; pointer-events:none; }
.voice-ring {
  inset:0px; border-radius:50%;
  border: 2px dashed #22d3ee66; opacity:.45; transform: scale(.92);
  transition: all .2s ease;
  margin-top: 40px; 
}
.voice-ring.on { border-color:#22c55e66; opacity:.85; transform: scale(1); }

.voice-viz {
  width: 720px; height: 150px; opacity:.85; filter: drop-shadow(0 8px 24px rgba(59,130,246,.15));
}

/* ===== Switches / Chips ===== */
.mode-switch { display:flex; justify-content:center; margin: 12px 0 4px; }
.stage-chips { display:flex; gap:8px; justify-content:center; margin:10px 0 18px; }
.stage-chips .v-chip { background:#0b1220; border:1px solid #273244; color:#cbd5e1; opacity:.7; }
.stage-chips .v-chip.on { border-color:#60a5fa; color:#e5f0ff; opacity:1; }

/* ===== Cards / Neon Edge ===== */
.section-card, .research-console, .neon {
  background: rgba(3,7,18,.6) !important;
  border:1px solid rgba(99,102,241,.25) !important;
  box-shadow: inset 0 0 0 1px rgba(99,102,241,.12), 0 8px 30px rgba(0,0,0,.45);
  border-radius: 16px;
}
.question-box >>> textarea { color:#e5e7eb; }

/* ===== Agent controls & steps ===== */
.agent-controls { display:flex; align-items:center; gap:12px; margin-top:10px; }
.agent-status .pill {
  padding:4px 10px; border-radius:999px; font-size:.76rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em;
  background:#1f2937; color:#cbd5e1; border:1px solid #374151;
}
.agent-status .pill.green { background:#064e3b; border-color:#059669; color:#a7f3d0; }
.agent-status .pill.red   { background:#4c0519; border-color:#be123c; color:#fecdd3; }
.agent-status .pill.blue  { background:#0b2447; border-color:#60a5fa; color:#cfe8ff; }
.agent-status .pill.grey  { background:#111827; border-color:#374151; color:#9ca3af; }

.agent-steps { padding-left:0; list-style:none; }
.agent-step {
  display:flex; align-items:flex-start; gap:10px; padding:8px 10px; margin:6px 0;
  border:1px dashed #334155; border-radius:10px; background: rgba(2,6,23,.4);
}
.agent-step .num {
  width:22px; height:22px; border-radius:6px; background:#0b1220; border:1px solid #475569;
  display:inline-flex; align-items:center; justify-content:center; font-size:.8rem; color:#cbd5e1;
}
.agent-step.pending  { opacity:.7; }
.agent-step.running  { border-color:#60a5fa; box-shadow:0 0 0 2px rgba(96,165,250,.18); background:linear-gradient(180deg, rgba(14,165,233,.15), rgba(2,6,23,.4)); }
.agent-step.done     { border-color:#22c55e; background:linear-gradient(180deg, rgba(34,197,94,.08), rgba(2,6,23,.4)); }

/* ===== Research Console ===== */
.rc-header { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border-bottom:1px solid #1f2937; }
.rc-title { font-size:1.05rem; font-weight:700; letter-spacing:.02em; color:#e5e7eb; }

.rc-tabs { display:flex; align-items:center; border-bottom:1px solid #1f2937; overflow:auto; }
.rc-tab {
  display:flex; align-items:center; gap:8px; padding:8px 12px; cursor:pointer;
  border-right:1px solid #0f172a; background:#0b1220; color:#cbd5e1;
}
.rc-tab.active { background:#0e1726; color:#fff; box-shadow: inset 0 -2px 0 #60a5fa; }
.rc-tab .dot { width:8px; height:8px; border-radius:50%; background:#475569; }
.rc-tab .dot.loading { background:#f59e0b; }
.rc-tab .dot.done    { background:#22c55e; }
.rc-tab .dot.error   { background:#ef4444; }
.rc-tab .label { max-width:220px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.rc-tabs .spacer { flex:1; }

.rc-body { display:grid; grid-template-columns: minmax(520px, 1fr) 380px; gap:16px; padding:12px; }
.rc-viewer { position:relative; border-radius:12px; overflow:hidden; border:1px solid #1f2937; background:#0b1220; }
.rc-viewer iframe { width:100%; height:640px; border:0; }

/* NEW: running-step glow on viewer */
.rc-viewer.step-running::after {
  content:""; position:absolute; inset:-2px; border-radius:12px;
  border: 2px solid rgba(96,165,250,.65);
  box-shadow: 0 0 24px rgba(96,165,250,.35), inset 0 0 20px rgba(96,165,250,.15);
  pointer-events:none; animation: ring 1.4s ease-in-out infinite;
}
@keyframes ring { 0%,100%{ opacity:.7 } 50%{ opacity: .35 } }

/* NEW: Agent overlay over the iframe */
.agent-overlay {
  position:absolute; left:12px; right:12px; top:12px; z-index:3;
  display:flex; align-items:center; gap:10px; pointer-events:none;
}
.agent-overlay .badge {
  font-weight:800; font-size:.72rem; letter-spacing:.06em; text-transform:uppercase;
  padding:6px 10px; border-radius:999px; border:1px solid #334155; background:#0b1220; color:#cbd5e1;
}
.agent-overlay .badge.green { background:#064e3b; border-color:#059669; color:#d1fae5; }
.agent-overlay .badge.red   { background:#4c0519; border-color:#be123c; color:#ffe4e6; }
.agent-overlay .badge.blue  { background:#0b2447; border-color:#60a5fa; color:#e0f2ff; }

.agent-overlay .current-step {
  display:flex; align-items:center; gap:10px; padding:8px 10px;
  background: linear-gradient(180deg, rgba(2,6,23,.9), rgba(2,6,23,.7));
  border:1px solid #334155; border-radius:12px; box-shadow: 0 6px 18px rgba(0,0,0,.4);
}
.agent-overlay .current-step .idx {
  display:inline-flex; align-items:center; justify-content:center;
  font-weight:800; font-size:.8rem; padding:2px 8px; border-radius:8px;
  background:#0b1220; border:1px solid #475569; color:#cbd5e1;
}
.agent-overlay .current-step .line { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; font-size:.82rem; color:#e5e7eb; }
.agent-overlay .hint { font-size:.8rem; color:#9ca3af; background:#0b1220cc; padding:6px 8px; border-radius:8px; border:1px solid #273244; }

/* Right side panels */
.rc-side { display:flex; flex-direction:column; gap:14px; }
.side-section { background:#0b1220; border:1px solid #1f2937; border-radius:12px; padding:10px; }
.side-title { font-weight:700; margin-bottom:8px; color:#e5e7eb; }
.results-grid { display:grid; gap:10px; }
.result-card {
  border:1px solid #223049; border-radius:10px; padding:10px;
  background:linear-gradient(180deg, rgba(3,7,18,.5), rgba(3,7,18,.35));
}
.result-card .rcard-title { font-weight:700; margin-bottom:4px; color:#e5f0ff; }
.result-card .rcard-snippet { font-size:.9rem; color:#b6c2d6; margin-bottom:8px; }
.result-card .rcard-meta { display:flex; align-items:center; gap:8px; color:#8aa0ba; }
.result-card .rcard-actions { margin-top:8px; display:flex; gap:8px; align-items:center; }
.open-link { color:#60a5fa; text-decoration:none; }

.timeline { display:flex; flex-direction:column; gap:8px; max-height:280px; overflow:auto; }
.tl-item { border:1px solid #1f2937; background:#0b1220; border-radius:10px; padding:8px; }
.tl-time { font-size:.75rem; color:#9ca3af; }
.tl-msg { color:#dbeafe; }

/* Anim headers / shimmer */
.anim-heading { font-weight:800; letter-spacing:.02em; color:#e5f0ff; }
.found-stream { display:grid; gap:8px; margin-top:8px; }
.found-item { border:1px solid #1f2937; background:#0b1220; border-radius:10px; padding:8px 10px; }
.found-item.shimmer { position:relative; overflow:hidden; }
.found-item.shimmer::after {
  content:""; position:absolute; inset:0; background:linear-gradient(90deg, transparent, rgba(255,255,255,.06), transparent);
  transform: translateX(-100%); animation: shimmer 1.6s ease infinite;
}
@keyframes shimmer { 100% { transform: translateX(100%); } }

/* Utilities */
.small { font-size:.86rem; }
.muted { color:#9ca3af; }
.warn { color:#fb7185; }
kbd { background:#0b1220; border:1px solid #334155; padding:2px 6px; border-radius:6px; font-size:.8rem; }

/* Responsive */
@media (max-width: 1100px) {
  .rc-body { grid-template-columns: 1fr; }
  .rc-viewer iframe { height: 520px; }
}
</style>


<style scoped>
/* Responsive, centered, neon glass vibe */
.voice-viz{
  display:block;
  width:min(1000px, 92%);
  height:260px;
  margin: 8px auto 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02));
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  box-shadow:
    0 10px 40px rgba(0, 240, 255, 0.10) inset,
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 8px 28px rgba(0,0,0,0.35);
  filter: drop-shadow(0 6px 16px rgba(0, 255, 230, .12));
  transition: box-shadow .25s ease, border-color .25s ease, opacity .25s ease;
  opacity: .98;
}
.voice-viz.on{
  box-shadow:
    0 12px 48px rgba(0, 240, 255, 0.14) inset,
    0 0 0 1px rgba(255,255,255,0.08) inset,
    0 10px 34px rgba(0,0,0,0.38);
  border-color: rgba(255,255,255,0.12);
}

/* Small screens */
@media (max-width: 960px){
  .voice-viz{ width: 92%; height: 220px; }
}
@media (max-width: 600px){
  .voice-viz{ height: 200px; }
}

/* ===========================
   NEXT-GEN RESEARCH BROWSER
   Neon glass + responsive data grid + no-dup UX
   =========================== */
/* equalizer bars under the waveform */
.voice-bars{
  display:block;
  width:min(560px, 92%);
  height:36px;
  margin:6px auto 0;
  filter: drop-shadow(0 4px 10px rgba(0, 255, 230, .12));
  opacity:.95;
}

/* ---------- BASE CONTAINER ---------- */
.voice-bars { display:none; }
.on + .voice-ring + .voice-wave + .voice-bars { display:block; }

.chat-container{
  --c1:#0a0f1f; --c2:#1b2a4a; --c3:#2e335a;
  --blob1:#00eaff; --blob2:#7b61ff; --blob3:#ff4d9d; --blob4:#00ffa3;

  position:relative;
  overflow:hidden;
  background: linear-gradient(135deg,var(--c1),var(--c2),var(--c3));
  background-size:300% 300%;
  animation: gradientIdle 14s ease-in-out infinite;

  padding:35px 25px;
  margin:0 auto;
  text-align:center; 
  min-height: 100vh;
  width:auto;
  transition: background 300ms ease, 
  background-position 300ms ease;
  will-change: background-position;
  box-shadow: inset 0 0 120px rgba(0,0,0,.25);
}

.is-thinking  { animation: gradientShift 8s ease-in-out infinite; }
.is-answering { animation: gradientShift 6s cubic-bezier(.22,.61,.36,1) infinite; }

/* Thinking (blue) – you already apply .is-thinking; force a calm blue gradient */
.is-thinking.chat-container{
  background: linear-gradient(135deg, #0b1b3a, #123b7a, #0b1b3a);
  background-size: 200% 200%;
  animation: thinkingBlue 6s ease-in-out infinite;
}
@keyframes thinkingBlue{
  0%{background-position:0% 50%}
  50%{background-position:100% 50%}
  100%{background-position:0% 50%}
}

/* Speaking (green) – bright responsive green */
.is-speaking.chat-container{
  background: linear-gradient(135deg, #073a2a, #0c8a5c, #073a2a);
  background-size: 220% 220%;
  animation: speakingGreen 2.8s ease-in-out infinite;
  box-shadow: inset 0 0 120px rgba(0,255,170,.12);
}
@keyframes speakingGreen{
  0%{background-position:20% 50%}
  50%{background-position:80% 50%}
  100%{background-position:20% 50%}
}

/* Answering – a more animated, “alive” spectrum */
.is-answering.chat-container{
  background: linear-gradient(135deg, #261247, #363aaa, #38763f, #00eaff, #261247);
  background-size: 400% 400%;
  animation: answeringFlow 8s cubic-bezier(.22,.61,.36,1) infinite;
}
@keyframes answeringFlow{
  0%{background-position:0% 50%}
  25%{background-position:100% 40%}
  50%{background-position:60% 60%}
  75%{background-position:20% 40%}
  100%{background-position:0% 50%}
}

/* Optional: when speaking, make blobs skew greenish for coherence */
.is-speaking .blobs { filter: hue-rotate(-40deg) saturate(120%); }
/* Optional: when thinking, cool them down */
.is-thinking .blobs { filter: hue-rotate(30deg) saturate(110%); }
/* Optional: when answering, let them glow a bit more */
.is-answering .blobs { opacity: .72; }

/* ---------- LAVA LAMP BLOB LAYER (DOM markup required)
   <div class="blobs" aria-hidden="true">
     <span class="blob b1"></span>
     <span class="blob b2"></span>
     <span class="blob b3"></span>
     <span class="blob b4"></span>
   </div>
------------------------------------- */
.blobs{
  position:absolute;
  inset:-10% -10% -20% -10%;
  filter: blur(38px) saturate(120%);
  mix-blend-mode: screen;
  opacity:.6;
  pointer-events:none;
  transform: translateZ(0);
}
.blob{
  position:absolute;
  width:42vmin; height:42vmin;
  border-radius:50%;
  box-shadow: 0 0 120px currentColor, 0 0 40px currentColor inset;
}
.b1{ color:var(--blob1); background:radial-gradient(circle at 40% 40%, #e6ffff 0 20%, currentColor 35%, transparent 70%); animation: blobFloat1 32s cubic-bezier(.33,0,.1,1) infinite; }
.b2{ color:var(--blob2); background:radial-gradient(circle at 60% 40%, #f2ebff 0 18%, currentColor 32%, transparent 70%); animation: blobFloat2 36s cubic-bezier(.33,0,.1,1) infinite -6s; }
.b3{ color:var(--blob3); background:radial-gradient(circle at 45% 55%, #ffe7f2 0 18%, currentColor 30%, transparent 70%); animation: blobFloat3 40s cubic-bezier(.33,0,.1,1) infinite -12s; }
.b4{ color:var(--blob4); background:radial-gradient(circle at 55% 45%, #eafff7 0 16%, currentColor 28%, transparent 70%); animation: blobFloat4 44s cubic-bezier(.33,0,.1,1) infinite -18s; }

/* ---------- KEYFRAMES ---------- */
@keyframes blobFloat1{0%{transform:translate3d(-10%,-12%,0) scale(1)}25%{transform:translate3d(18%,-18%,0) scale(1.08)}50%{transform:translate3d(26%,8%,0) scale(1.02)}75%{transform:translate3d(-6%,16%,0) scale(1.1)}100%{transform:translate3d(-10%,-12%,0) scale(1)}}
@keyframes blobFloat2{0%{transform:translate3d(40%,-8%,0) scale(1)}25%{transform:translate3d(12%,10%,0) scale(1.06)}50%{transform:translate3d(-16%,24%,0) scale(1.12)}75%{transform:translate3d(22%,6%,0) scale(1.04)}100%{transform:translate3d(40%,-8%,0) scale(1)}}
@keyframes blobFloat3{0%{transform:translate3d(-24%,28%,0) scale(1.06)}25%{transform:translate3d(10%,22%,0) scale(1)}50%{transform:translate3d(34%,10%,0) scale(1.08)}75%{transform:translate3d(-12%,-6%,0) scale(1.02)}100%{transform:translate3d(-24%,28%,0) scale(1.06)}}
@keyframes blobFloat4{0%{transform:translate3d(22%,36%,0) scale(1.04)}25%{transform:translate3d(-8%,18%,0) scale(1.1)}50%{transform:translate3d(-28%,-8%,0) scale(1.02)}75%{transform:translate3d(8%,-18%,0) scale(1.08)}100%{transform:translate3d(22%,36%,0) scale(1.04)}}
@keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes gradientIdle{0%{background-position:20% 50%}50%{background-position:80% 50%}100%{background-position:20% 50%}}

/* ---------- Avatar + Wave ---------- */
.avatar-container {
  position: relative;
  margin: 0 auto 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.ai-avatar {
  width: 160px; height: 160px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,.28);
  background: rgba(20, 28, 44, 0.35);
  transition: transform .35s ease, box-shadow .35s ease, filter .35s ease;
  box-shadow: 0 6px 24px rgba(0, 255, 240, 0.10), 0 0 0 1px rgba(255,255,255,.04) inset;
}
.ai-avatar.thinking { transform: scale(1.05); box-shadow: 0 12px 40px rgba(0,255,240,.18), 0 0 0 1px rgba(255,255,255,.06) inset; }

.pulse-glow {
  position: absolute;
  width: 150px; height: 150px;
  top: -12px; left: 50%; transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(45% 45% at 50% 50%, rgba(0,255,230,.18), transparent 70%);
  animation: halo 2.4s ease-in-out infinite;
  filter: blur(2px);
  pointer-events: none;
}
.pulse-glow.thinking { animation: halo-fast 1.2s ease-in-out infinite; }
@keyframes halo { 0%,100% { transform:translateX(-50%) scale(.94); opacity:.55; } 50% { transform:translateX(-50%) scale(1.06); opacity:.95; } }
@keyframes halo-fast { 0%,100% { transform:translateX(-50%) scale(.98); opacity:.60; } 50% { transform:translateX(-50%) scale(1.10); opacity:1; } }

.mic-wrap { position: relative; display:flex; align-items:center; justify-content:center; }
.mic-btn {
  width: 46px; height: 46px; border-radius: 50%;
  border: 1px solid rgba(255,255,255,.10);
  background: rgba(255,255,255,.06);
  color:#e6fbff; cursor:pointer;
  transition: transform .20s ease, box-shadow .25s ease, background .25s ease;
  backdrop-filter: blur(6px);
}
.mic-btn.on { box-shadow: 0 0 0 6px rgba(0,230,255,.18), 0 0 22px rgba(0,230,255,.45) inset; transform: scale(1.04); }
.mic-btn.disabled { opacity:.4; cursor:not-allowed; }
.voice-ring {
  position:absolute; inset:0px; border-radius: 50%;
  border: 1px dashed rgba(120, 241, 255, .55);
  opacity: 0; transform: scale(.9);
  transition: opacity .25s ease, transform .25s ease;
}
.voice-ring.on { opacity: .9; }

.voice-wave {
  display:block;
  width: min(380px, 92%);
  height: 120px;
  margin: 2px auto 0;
  filter: drop-shadow(0 6px 16px rgba(0, 255, 230, .18));
}

/* ---------- Top guidance ---------- */
.subtitle { opacity:.75; font-size:.95rem; margin: 6px 0 12px; }
.voice-cta-wrap { margin: 6px 0 10px; display:flex; align-items:center; gap:10px; justify-content:center; }
.voice-cta { text-transform:none; font-weight:600; }
.voice-hint { opacity:.8; }
.voice-hint.warn { color:#ffcc80; }

/* ---------- Mode & Stage ---------- */
.mode-switch { display:flex; justify-content:center; margin-bottom:12px; }
.mode-switch .v-btn.active { background:rgba(255,255,255,.18)!important; }
.stage-chips { display:flex; justify-content:center; gap:8px; margin-bottom:10px; }
.stage-chips .v-chip { opacity:.5; transition:transform .25s ease, opacity .25s; }
.stage-chips .v-chip.on { opacity:1; transform:scale(1.04); box-shadow:0 0 10px rgba(255,255,255,.3); }

/* ---------- Cards / Inputs ---------- */
.section-card { background:rgba(255,255,255,.06); border-radius:12px; padding:25px; margin-bottom:20px; backdrop-filter: blur(4px); }
.section-card.neon { box-shadow: 0 10px 40px #00f0ff12 inset, 0 0 0 1px #ffffff08 inset; }
.input-row { display:flex; gap:10px; align-items:flex-start; }
.question-box { background: rgba(255,255,255,.08); border-radius: 12px; }
.ask-btn { height: 44px; align-self:flex-start; }

.voice-caption { text-align:left; margin-top:8px; background: rgba(0,240,255,.06); border: 1px solid rgba(0,240,255,.15); border-radius: 10px; padding: 8px 10px; }
.voice-caption .cap-title { font-size:.85rem; opacity:.9; }
.voice-caption .cap-line { margin-top:4px; }
.voice-caption .final { margin-right: 6px; }
.voice-caption .interim { opacity:.7; font-style: italic; }

/* ---------- Research Console ---------- */
.research-console { background: rgba(255,255,255,.06); border-radius: 12px; padding: 0; }
.rc-header { display:flex; align-items:center; justify-content:space-between; padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,.08); }
.rc-title { font-weight: 700; }
.rc-tabs { display:flex; align-items:center; gap:6px; padding: 8px 10px; flex-wrap: wrap; border-bottom: 1px solid rgba(255,255,255,.07); }
.rc-tab { display:flex; align-items:center; gap:6px; padding:6px 10px; border-radius:10px; background:rgba(255,255,255,.05); cursor:pointer; transition:transform .15s ease, box-shadow .2s ease; }
.rc-tab:hover { transform: translateY(-1px); box-shadow: 0 8px 24px #00f0ff12; }
.rc-tab.active { background:rgba(255,255,255,.12); box-shadow:0 0 14px rgba(255,255,255,.18); }
.rc-tab .dot { width:8px; height:8px; border-radius:50%; background:#aaa; }
.rc-tab .dot.loading { background:#ffcf33; box-shadow:0 0 10px #ffcf33; }
.rc-tab .dot.done { background:#00ffa3; box-shadow:0 0 10px #00ffa3; }
.rc-tab .dot.error { background:#ff4d4d; box-shadow:0 0 10px #ff4d4d; }
.rc-tab .label { max-width: 210px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rc-tabs .spacer { flex:1; }

.rc-body { display:grid; grid-template-columns: 1.35fr .9fr; gap: 10px; padding: 10px; }
.rc-viewer { background: rgba(0,0,0,.18); border-radius: 10px; min-height: 360px; overflow: hidden; border:1px solid rgba(255,255,255,.08); }
.rc-viewer iframe { width:100%; height:520px; border:0; background:#111; }
.viewer-empty { display:flex; align-items:center; justify-content:center; height: 360px; color: #bbb; }

.rc-side { display:flex; flex-direction:column; gap:10px; }
.side-section { background: rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.08); border-radius: 10px; padding: 10px; }
.side-title { font-weight: 700; margin-bottom: 6px; }

/* ---------- Responsive result grids ---------- */
.results-grid, .data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
}
.result-card {
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 12px;
  padding: 12px;
  text-align: left;
  transition: transform .15s ease, box-shadow .2s ease, border-color .2s ease;
}
.result-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 255, 230, .12);
  border-color: rgba(0, 255, 230, .25);
}
.result-card.big { padding: 14px; }
.rcard-title { font-weight: 700; font-size: .98rem; margin-bottom: 6px; line-height: 1.2; }
.rcard-snippet { opacity: .88; font-size: .9rem; margin-bottom: 8px; }
.rcard-meta { display:flex; align-items:center; gap:8px; font-size:.8rem; opacity:.8; margin-bottom: 8px; }
.rcard-meta .host { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; opacity:.8; }
.rcard-actions { display:flex; align-items:center; gap:8px; flex-wrap: wrap; }
.rcard-actions .open-link { font-size:.85rem; opacity:.9; }

.timeline { max-height: 240px; overflow:auto; padding-right: 2px; }
.tl-item { display:grid; grid-template-columns: 80px 1fr; gap:8px; padding:6px 0; border-bottom:1px dashed rgba(255,255,255,.06); }
.tl-item:last-child { border-bottom:0; }
.tl-time { opacity:.7; font-size:.8rem; }
.tl-msg { font-size:.92rem; }

/* ---------- Search preview ---------- */
.anim-heading { display:flex; align-items:center; font-weight:600; }
.found-stream { display:grid; grid-template-columns: repeat(auto-fill,minmax(220px,1fr)); gap:10px; margin-top:8px; }
.found-item { background: rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.08); border-radius: 10px; padding: 8px; }
.found-item .title { font-weight:600; margin-bottom:4px; }
.found-item .snippet { opacity:.85; font-size:.9rem; }
.shimmer { position:relative; overflow:hidden; }
.shimmer::after { content:""; position:absolute; inset:0; transform:translateX(-100%); background: linear-gradient(120deg, transparent, rgba(255,255,255,.10), transparent); animation: shimmer 1.6s infinite; }
@keyframes shimmer { 100%{ transform:translateX(100%); } }

/* ---------- Grain Overlay ---------- */
.chat-container::after{
  content:"";
  position:absolute; inset:0;
  background-image:
    radial-gradient(rgba(255,255,255,.04) 1px, transparent 1.2px),
    radial-gradient(rgba(255,255,255,.03) 1px, transparent 1.2px);
  background-size: 120px 120px, 80px 80px;
  background-position: 0 0, 40px 60px;
  mix-blend-mode: overlay;
  opacity:.25;
  pointer-events:none;
}

/* ---------- Responsive ---------- */
@media (max-width: 1200px){
  .rc-body { grid-template-columns: 1fr; }
}
@media (max-width: 960px){
  .chat-container{ padding:18px 14px; }
  .ai-avatar { width:84px; height:84px; }
  .pulse-glow { width:112px; height:112px; left:calc(50% - 56px); top:-10px; }
  .voice-wave { width: 92%; }
}
@media (max-width: 600px){
  .ai-avatar { width:84px; height:84px; }
  .pulse-glow { width:112px; height:112px; left:calc(50% - 56px); top:-10px; }
}
@media (prefers-reduced-motion: reduce){
  .chat-container, .blobs, .blob, .pulse-glow { animation: none !important; }
}


</style>
